import logger from '@/utils/logger';
'use client'

import React, { useState, useEffect, useMemo } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Button } from '../components/ui/button'
import { getLoginUrl } from '../utils/domain';
// Interface ultra-fluide style OpenAI
import FluidChat from '../components/FluidChat'
import PDFViewer from '../components/PDFViewer'
import DataVisualization from '../components/DataVisualization'
import { useAuth } from '../components/auth/AuthProvider'
import useAuthReady from '../hooks/useAuthReady'
import LoginForm from '../components/auth/LoginForm'
import UserMenu from '../components/UserMenu'
import TokenCounter from '../components/TokenCounter'
import { useTokens } from '../hooks/useTokens'
import InsufficientTokensDeepReportModal from '../components/billing/InsufficientTokensDeepReportModal'
import ThemeToggle from '../components/ThemeToggle'
import jsPDF from 'jspdf'
import html2canvas from 'html2canvas'
import { downloadPDF } from '../utils/pdfExportV3'
import AnalysisProgress from '../components/studio/AnalysisProgress'
import DataSourceManager from '../components/studio/DataSourceManager'
import InsightViewer from '../components/studio/InsightViewer'
import DeepReportGenerator from '../components/studio/DeepReportGenerator'
import DeepReportRenderer from '../components/studio/DeepReportRenderer'
import PDFPreviewV3 from '../components/PDFPreviewV3'
import AdvancedAnalytics from '../components/studio/AdvancedAnalytics'
import BigDataAnalytics from '../components/studio/BigDataAnalytics'
import FileManager from '../components/studio/FileManager'
import InsightsDashboard from '../components/studio/InsightsDashboard'
import BusinessPattern from '../components/BusinessPattern'
import { supabase, getInsights, getAnalysisJobs, getDataImports, subscribeToInsights, subscribeToChatSessions, Insight, AnalysisJob, DataImport, saveDeepReportRPC, recoverSession } from '../lib/supabase'
import { API_URL } from '../lib/config'
import { authFetch } from '../utils/api-headers'
import { DataManager } from '../lib/data-manager'
import { RealtimeManager } from '../lib/realtime-manager'
import CreationSpark from '../components/brand/CreationSpark'
import Logo from '../components/brand/Logo'
import {
 FileText,
 Database,
 Brain, 
 Zap,
 MessageSquare,
 Plus,
 Upload,
 BarChart3,
 Settings,
 Search,
 Filter,
 Clock,
 BookOpen,
 Folder,
 Sparkles,
 Edit3,
 Edit2,
 Trash2,
 Download,
 Server,
 Activity,
 ChevronDown,
 Menu,
 AlertTriangle,
 CheckCircle,
 ExternalLink,
 X
} from 'lucide-react'

// FONCTION POUR PARSER LES SPECIFICATION JSON DES DEEP REPORTS
function parseSpecificationJSON(content: string): any[] {
 const charts: any[] = [];
 
 // Recherche plus robuste des blocs JSON
 const lines = content.split('\n');
 let inJsonBlock = false;
 let jsonBuffer = '';
 let braceCount = 0;
 
 for (const line of lines) {
 if (line.includes('SPECIFICATION JSON:')) {
 inJsonBlock = true;
 jsonBuffer = '';
 braceCount = 0;
 // Extraire le JSON qui suit sur la même ligne
 const jsonStart = line.indexOf('{');
 if (jsonStart !== -1) {
 jsonBuffer = line.substring(jsonStart);
 braceCount = (jsonBuffer.match(/\{/g) || []).length - (jsonBuffer.match(/\}/g) || []).length;
 }
 } else if (inJsonBlock) {
 jsonBuffer += '\n' + line;
 braceCount += (line.match(/\{/g) || []).length - (line.match(/\}/g) || []).length;
 
 if (braceCount <= 0) {
 // JSON complet trouvé
 try {
 const cleanJson = jsonBuffer.replace(/[\n\r\t]/g, ' ').trim();
 const chartData = JSON.parse(cleanJson);
 
 // Convertir au format attendu par DataVisualization
 const convertedChart = {
 type: chartData.type || 'bar',
 title: chartData.title || 'Graphique',
 data: {
 labels: chartData.data?.map((item: any) => item.label || item.name) || [],
 values: chartData.data?.map((item: any) => item.value || item.y) || []
 },
 insight: chartData.insight || 'Graphique généré par Deep Report',
 source: {
 fileName: 'Deep Report',
 createdAt: new Date()
 },
 interactive: true
 };
 
 charts.push(convertedChart);
 
 } catch (error) {
 logger.error('Erreur parsing JSON chart:', error);
 }
 
 inJsonBlock = false;
 jsonBuffer = '';
 }
 }
 }
 
 return charts;
}

interface Tab {
 id: string;
 label: string;
 icon: any;
 badge?: string;
}

export default function Studio() {
 // CORRECTION RACE CONDITION: Utiliser useAuthReady pour garantir user + profile chargés
 // Protection contre les erreurs de hot reload
 let isReady = false, user = null, profile = null, loading = true;
 
 try {
 const authState = useAuthReady();
 isReady = authState.isReady;
 user = authState.user;
 profile = authState.profile;
 loading = authState.loading;
 } catch (error) {
 // En cas d'erreur (hot reload, etc.), utiliser useAuth directement
 const authState = useAuth();
 isReady = !authState.loading && authState.user != null && authState.profile != null;
 user = authState.user;
 profile = authState.profile;
 loading = authState.loading;
 }
 
 const navigate = useNavigate()
 const [showLogin, setShowLogin] = useState(false)
 const { balance, hasEnoughTokens, subscriptionTier, refresh: refreshTokens } = useTokens()
 const [showInsufficientTokensModal, setShowInsufficientTokensModal] = useState(false)
 const [showUpgradeModalMemory, setShowUpgradeModalMemory] = useState(false)
 const [requiredTokensForDeepReport, setRequiredTokensForDeepReport] = useState(0)
 const [selectedInsightsForModal, setSelectedInsightsForModal] = useState(0)
 // PERSISTANCE ONGLET : Restaurer l'onglet depuis localStorage ou défaut intelligent
 const getInitialTab = () => {
 const savedTab = localStorage.getItem('clairify_active_tab');
 if (savedTab && ['insights', 'deep-report', 'data-viz', 'big-data', 'memory-patterns', 'ai-console'].includes(savedTab)) {
 return savedTab as 'insights' | 'deep-report' | 'data-viz' | 'big-data' | 'memory-patterns' | 'ai-console';
 }
 // Logique intelligente : ai-console pour première visite, insights si data existante
 const hasExistingData = localStorage.getItem('clairify_last_session_id');
 return hasExistingData ? 'insights' : 'ai-console';
 };
 
 const [currentTab, setCurrentTab] = useState<'insights' | 'deep-report' | 'data-viz' | 'big-data' | 'memory-patterns' | 'ai-console'>(getInitialTab())
 
 // Sub-tab pour Memory Patterns
 const [memorySubTab, setMemorySubTab] = useState<'patterns' | 'dna' | 'collisions'>('patterns')
 
 // Filtres et pagination pour patterns
 const [patternFilter, setPatternFilter] = useState({
 type: 'all',
 status: 'all',
 search: '',
 sortBy: 'impact' // impact, confidence, date, occurrences
 })
 const [currentPage, setCurrentPage] = useState(1)
 const patternsPerPage = 20
 
 // WRAPPER PERSISTANCE : Sauvegarder l'onglet à chaque changement
 const switchToTab = (newTab: 'insights' | 'deep-report' | 'data-viz' | 'big-data' | 'memory-patterns' | 'ai-console') => {
 setCurrentTab(newTab);
 localStorage.setItem('clairify_active_tab', newTab);
 
 // LOGIQUE SPÉCIALE AI-CONSOLE : Auto nouveau chat si première visite
 if (newTab === 'ai-console') {
 const hasActiveSession = localStorage.getItem('clairify_active_chat_session');
 if (!hasActiveSession && !activeSessionId) {
 // Le nouveau chat sera créé automatiquement par FluidChat au premier message
 setActiveSessionId(null); // Reset pour forcer nouveau chat
 }
 }
 };
 
 // Une seule console - RevolutionaryAIChat optimisée
 const [sidebarSection, setSidebarSection] = useState<'files' | 'workbench' | 'knowledge' | 'history'>('files')
 const [insights, setInsights] = useState<Insight[]>([])
 const [analysisJobs, setAnalysisJobs] = useState<AnalysisJob[]>([])
 const [currentAnalysis, setCurrentAnalysis] = useState<AnalysisJob | null>(null)
 const [files, setFiles] = useState<DataImport[]>([])
 const [terminalOpen, setTerminalOpen] = useState(false)
 const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false)
 const [markdownContent, setMarkdownContent] = useState('# Rapport d\'analyse\n\nVos insights générés par l\'IA apparaîtront ici pour édition...')
 const [chatSessions, setChatSessions] = useState<any[]>([])
 const [pdfViewerOpen, setPdfViewerOpen] = useState(false)
 const [currentPdfUrl, setCurrentPdfUrl] = useState<string | null>(null)
 const [consoleInsights, setConsoleInsights] = useState<any[]>([])
 const [isGeneratingDeepReport, setIsGeneratingDeepReport] = useState(false)
 const [deepReportProgress, setDeepReportProgress] = useState(0)
 const [hasNewDeepReport, setHasNewDeepReport] = useState(false)
 const [showPDFPreview, setShowPDFPreview] = useState(false)
 const [aiGeneratedCharts, setAiGeneratedCharts] = useState<any[]>([])
 const [currentDeepReport, setCurrentDeepReport] = useState<any>(null)
 
 // Vérification: Minimum 3 analyses pour débloquer Memory Patterns (useMemo pour recalcul dynamique)
 const sessionsWithInsights = useMemo(() => chatSessions.filter(s => s.insights_count > 0).length, [chatSessions])
 const canUseMemoryPatterns = useMemo(() => sessionsWithInsights >= 3, [sessionsWithInsights])
 
 // MEMORY LAYER: Business Patterns
 const [businessPatterns, setBusinessPatterns] = useState<any[]>([])
 const [patternsLoading, setPatternLoading] = useState(false)
 const [patternsError, setPatternError] = useState<string | null>(null)
 
 // INLINE RENAME/DELETE - PAS DE MODAL
 const [editingSessionId, setEditingSessionId] = useState<string | null>(null)
 const [editingTitle, setEditingTitle] = useState('')
 
 // OPTIMISATION: Guards + Cache pour performances ultra-rapides
 const loadingSessionsRef = React.useRef(false)
 const sessionsLoadedRef = React.useRef(false)
 const SESSIONS_CACHE_KEY = 'clairify_sessions_cache'
 const CACHE_DURATION = 5 * 60 * 1000 // 5 minutes
 
 
 // Fonction de filtrage et tri des patterns (APRÈS businessPatterns)
 const getFilteredAndSortedPatterns = () => {
 let filtered = [...businessPatterns]
 
 // Filtre par type
 if (patternFilter.type !== 'all') {
 filtered = filtered.filter(p => p.pattern_type === patternFilter.type)
 }
 
 // Filtre par statut
 if (patternFilter.status === 'validated') {
 filtered = filtered.filter(p => p.is_validated_by_user)
 } else if (patternFilter.status === 'pending') {
 filtered = filtered.filter(p => !p.is_validated_by_user)
 }
 
 // Recherche textuelle
 if (patternFilter.search) {
 const search = patternFilter.search.toLowerCase()
 filtered = filtered.filter(p => 
 p.title?.toLowerCase().includes(search) ||
 p.description?.toLowerCase().includes(search)
 )
 }
 
 // Tri
 filtered.sort((a, b) => {
 switch (patternFilter.sortBy) {
 case 'impact':
 return Math.abs(b.estimated_impact_eur || 0) - Math.abs(a.estimated_impact_eur || 0)
 case 'confidence':
 return b.confidence_score - a.confidence_score
 case 'date':
 return new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
 case 'occurrences':
 return b.occurrences_count - a.occurrences_count
 default:
 return 0
 }
 })
 
 return filtered
 }
 
 // Pagination
 const getPaginatedPatterns = () => {
 const filtered = getFilteredAndSortedPatterns()
 const startIndex = (currentPage - 1) * patternsPerPage
 const endIndex = startIndex + patternsPerPage
 return filtered.slice(startIndex, endIndex)
 }
 
 const totalPages = Math.ceil(getFilteredAndSortedPatterns().length / patternsPerPage)
 
 // INNOVATION 1: Pattern Collisions
 const [patternCollisions, setPatternCollisions] = useState<any[]>([])
 const [collisionsLoading, setCollisionsLoading] = useState(false)
 const [collisionsFilter, setCollisionsFilter] = useState<'all' | 'active' | 'resolved'>('active')
 const [syncingNotion, setSyncingNotion] = useState<Record<string, boolean>>({})
 
 // INNOVATION 5: DNA Score
 const [maturityScore, setMaturityScore] = useState<any | null>(null)
 const [scoreLoading, setScoreLoading] = useState(false)
 
 // OAuth Notion
 const [notionIntegration, setNotionIntegration] = useState<any | null>(null)
 
 const [sessionDeepReports, setSessionDeepReports] = useState<any[]>([])
 const [selectedDeepReportIndex, setSelectedDeepReportIndex] = useState(0)
 const [activeSessionId, setActiveSessionId] = useState<string | null>(null)

 // NOUVEAU: État pour la gestion de la connectivité temps réel
 const [isOnline, setIsOnline] = useState(true)
 const [lastSync, setLastSync] = useState<Date>(new Date())

 // NOUVEAU: Fonction de diagnostic de connectivité
 const checkConnectivity = async () => {
 try {
 const { data: session } = await supabase.auth.getSession();
 const isConnected = !!session?.session;

 logger.log('[Connectivity] Session active:', isConnected);
 logger.log('[Connectivity] Online:', isOnline);
 logger.log('[Connectivity] Last sync:', lastSync.toISOString());

 return {
 sessionActive: isConnected,
 networkOnline: isOnline,
 lastSync: lastSync,
 sessionsCount: chatSessions.length
 };
 } catch (error) {
 logger.error('[Connectivity] Erreur diagnostic:', error);
 return null;
 }
 };

 // Fonction pour charger les sessions depuis l'API
 const loadChatSessions = async (forceRefresh = false) => {
 if (!user?.id) {
 return [];
 }

 // GUARD: Éviter les appels multiples (React Strict Mode)
 if (loadingSessionsRef.current && !forceRefresh) {
 logger.log('[loadChatSessions] Déjà en cours, ignoré');
 return [];
 }
 
 loadingSessionsRef.current = true;
 
 try {
 // DataManager gère TOUT: Memory → IndexedDB → Supabase → API
 logger.log('[loadChatSessions] Using DataManager...');
 const sessions = await DataManager.getSessions(user.id, forceRefresh);
 
 if (sessions && sessions.length > 0) {
 // Transformer pour l'interface (compatibilité legacy)
 const transformedSessions = sessions.map((session: any) => ({
 id: session.id,
 title: session.title,
 name: session.title,
 messages: [], // Les messages seront chargés à la demande
 active: false,
 created_at: session.created_at,
 last_activity: session.last_activity,
 insights_count: session.insights_count || 0,
 charts_count: session.charts_count || 0,
 has_deep_report: session.has_deep_report || false,
 reports_count: session.reports_count || 0
 }));
 
 setChatSessions(transformedSessions);
 sessionsLoadedRef.current = true;
 logger.log('[loadChatSessions] Sessions chargées via DataManager');
 return transformedSessions; // RETOURNER pour usage dans Promise.all()
 } else {
 setChatSessions([]);
 logger.log('[loadChatSessions] Aucune session disponible');
 return [];
 }
 } catch (error) {
 logger.error('Studio: Erreur chargement sessions via DataManager:', error);
 setChatSessions([]);
 sessionsLoadedRef.current = true;
 return [];
 } finally {
 loadingSessionsRef.current = false;
 }
 };

 // ============================================================================
 // MEMORY LAYER: Fonctions pour gérer les Business Patterns
 // ============================================================================
 
 const loadBusinessPatterns = async () => {
 if (!user?.id) return;
 
 try {
 // SÉCURITÉ: JWT automatique via authFetch
 const response = await authFetch(`${API_URL}/api/patterns/${user.id}`);
 
 if (!response.ok) {
 throw new Error(`Erreur ${response.status}`);
 }
 
 const data = await response.json();
 setBusinessPatterns(data.patterns || []);
 } catch (error) {
 logger.error('Erreur chargement patterns:', error);
 setPatternError('Impossible de charger les patterns');
 }
 };
 
 const triggerPatternDetection = async () => {
 if (!user?.id) return;
 
 // RESTRICTION FREE: Pas d'accès aux analyses Memory Pattern
 if (subscriptionTier === 'free') {
 setShowUpgradeModalMemory(true);
 return;
 }
 
 // RESTRICTION: Minimum 3 analyses FluidChat requises
 const sessionsWithInsights = chatSessions.filter(s => s.insights_count > 0).length;
 if (sessionsWithInsights < 3) {
 // Message déjà affiché dans l'UI, pas besoin d'alert
 return;
 }
 
 setPatternLoading(true);
 setPatternError(null);
 
 try {
 // SÉCURITÉ: Utiliser authFetch pour JWT
 const response = await authFetch(`${API_URL}/api/patterns/detect`, {
 method: 'POST',
 body: JSON.stringify({ 
 userId: user.id, 
 sessionLimit: 20,
 forceRefresh: true 
 })
 });
 
 if (!response.ok) {
 throw new Error(`Erreur ${response.status}`);
 }
 
 const data = await response.json();
 
 // Recharger les patterns
 await loadBusinessPatterns();
 
 // Rafraîchir la balance tokens
 await refreshTokens();
 } catch (error) {
 logger.error('Erreur détection patterns:', error);
 setPatternError('Échec de la détection');
 } finally {
 setPatternLoading(false);
 }
 };
 
 const validatePattern = async (patternId: string, isValidated: boolean, feedback?: string) => {
 try {
 // SÉCURITÉ: JWT automatique via authFetch
 const response = await authFetch(`${API_URL}/api/patterns/${patternId}/validate`, {
 method: 'PATCH',
 body: JSON.stringify({ isValidated, feedback })
 });
 
 if (!response.ok) {
 throw new Error(`Erreur ${response.status}`);
 }
 
 
 // Recharger les patterns
 await loadBusinessPatterns();
 } catch (error) {
 logger.error('Erreur validation pattern:', error);
 }
 };
 
 const deletePattern = async (patternId: string) => {
 try {
 // SÉCURITÉ: JWT automatique via authFetch
 const response = await authFetch(`${API_URL}/api/patterns/${patternId}`, {
 method: 'DELETE'
 });
 
 if (!response.ok) {
 throw new Error(`Erreur ${response.status}`);
 }
 
 
 // Recharger les patterns
 await loadBusinessPatterns();
 } catch (error) {
 logger.error('Erreur suppression pattern:', error);
 }
 };
 
 const createNotionAction = async (patternId: string) => {
 try {
 // SÉCURITÉ: JWT automatique via authFetch
 const response = await authFetch(`${API_URL}/api/patterns/${patternId}/create-notion-action`, {
 method: 'POST'
 });
 
 if (!response.ok) {
 const error = await response.json();
 throw new Error(error.message || `Erreur ${response.status}`);
 }
 
 const data = await response.json();
 
 // SÉCURITÉ: Notification avec DOM API (pas innerHTML)
 const notification = document.createElement('div');
 notification.className = 'fixed top-4 right-4 z-50 bg-gradient-to-r from-purple-600 to-indigo-600 text-theme-primary px-6 py-3 rounded-lg shadow-xl transition-all duration-300';
 
 const container = document.createElement('div');
 container.className = 'flex items-center space-x-3';
 
 const iconWrapper = document.createElement('div');
 iconWrapper.className = 'w-6 h-6 bg-theme-surface rounded-full flex items-center justify-center';
 const iconInner = document.createElement('div');
 iconInner.className = 'w-3 h-3 bg-accent-violet rounded-full';
 iconWrapper.appendChild(iconInner);
 
 const textWrapper = document.createElement('div');
 const title = document.createElement('div');
 title.className = 'font-bold';
 title.textContent = 'Action créée dans Notion!';
 const subtitle = document.createElement('div');
 subtitle.className = 'text-sm opacity-90';
 subtitle.textContent = 'Consultez votre workspace Notion';
 textWrapper.appendChild(title);
 textWrapper.appendChild(subtitle);
 
 container.appendChild(iconWrapper);
 container.appendChild(textWrapper);
 notification.appendChild(container);
 document.body.appendChild(notification);
 setTimeout(() => notification.remove(), 4000);
 
 // Recharger les patterns pour afficher le badge "Dans Notion"
 await loadBusinessPatterns();
 } catch (error: any) {
 logger.error('Erreur création action Notion:', error);
 
 // SÉCURITÉ: Notification error avec DOM API (pas innerHTML)
 const notification = document.createElement('div');
 notification.className = 'fixed top-4 right-4 z-50 bg-red-500 text-theme-primary px-6 py-3 rounded-lg shadow-xl';
 
 const errorTitle = document.createElement('div');
 errorTitle.className = 'font-bold';
 errorTitle.textContent = 'Erreur Notion';
 
 const errorText = document.createElement('div');
 errorText.className = 'text-sm';
 errorText.textContent = error.message || 'Vérifiez votre configuration';
 
 notification.appendChild(errorTitle);
 notification.appendChild(errorText);
 document.body.appendChild(notification);
 setTimeout(() => notification.remove(), 5000);
 }
 };

 // ============================================================================
 // INNOVATION 1: Pattern Collisions Functions
 // ============================================================================
 
 const loadCollisions = async () => {
 if (!user?.id) return;
 
 setCollisionsLoading(true);
 try {
 // SÉCURITÉ: JWT automatique via authFetch
 // IMPORTANT: includeResolved=true pour avoir TOUTES les collisions (filtrage côté frontend)
 const response = await authFetch(`${API_URL}/api/patterns/collisions/${user.id}?includeResolved=true`);
 if (!response.ok) throw new Error(`Erreur ${response.status}`);
 
 const data = await response.json();
 setPatternCollisions(data.collisions || []);
 } catch (error) {
 logger.error('Erreur chargement collisions:', error);
 } finally {
 setCollisionsLoading(false);
 }
 };
 
 const detectCollisions = async () => {
 if (!user?.id) return;
 
 // RESTRICTION FREE: Pas d'accès aux analyses Memory Pattern
 if (subscriptionTier === 'free') {
 setShowUpgradeModalMemory(true);
 return;
 }
 
 // RESTRICTION: Minimum 3 analyses FluidChat requises
 const sessionsWithInsights = chatSessions.filter(s => s.insights_count > 0).length;
 if (sessionsWithInsights < 3) {
 // Message déjà affiché dans l'UI, pas besoin d'alert
 return;
 }
 
 setCollisionsLoading(true);
 try {
 // SÉCURITÉ: JWT automatique via authFetch
 const response = await authFetch(`${API_URL}/api/patterns/detect-collisions`, {
 method: 'POST',
 body: JSON.stringify({ userId: user.id })
 });
 
 if (!response.ok) throw new Error(`Erreur ${response.status}`);
 
 const data = await response.json();
 await loadCollisions();
 
 // Rafraîchir la balance tokens
 await refreshTokens();
 } catch (error) {
 logger.error('Erreur détection collisions:', error);
 } finally {
 setCollisionsLoading(false);
 }
 };
 
 const resolveCollision = async (collisionId: string) => {
 try {
 // SÉCURITÉ: JWT automatique via authFetch
 const response = await authFetch(`${API_URL}/api/patterns/collisions/${collisionId}/resolve`, {
 method: 'PATCH',
 body: JSON.stringify({ userId: user?.id })
 });
 
 if (!response.ok) throw new Error(`Erreur ${response.status}`);
 await loadCollisions();
 
 // APPRENTISSAGE IA: Rafraîchir patterns après résolution
 await loadBusinessPatterns();
 } catch (error) {
 logger.error('Erreur résolution collision:', error);
 }
 };
 
 const syncCollisionToNotion = async (collisionId: string) => {
 if (!notionIntegration) {
 // Notification erreur: pas de Notion connecté
 const notification = document.createElement('div');
 notification.className = 'fixed top-4 right-4 z-50 bg-purple-600 dark:bg-purple-500 text-white px-6 py-3 rounded-lg shadow-xl';
 const errorTitle = document.createElement('div');
 errorTitle.className = 'font-bold';
 errorTitle.textContent = 'Notion non connecté';
 const errorText = document.createElement('div');
 errorText.className = 'text-sm';
 errorText.textContent = 'Connectez d\'abord votre workspace Notion';
 notification.appendChild(errorTitle);
 notification.appendChild(errorText);
 document.body.appendChild(notification);
 setTimeout(() => notification.remove(), 5000);
 return;
 }
 
 setSyncingNotion(prev => ({ ...prev, [collisionId]: true }));
 try {
 const response = await authFetch(`${API_URL}/api/collisions/${collisionId}/sync-notion`, {
 method: 'POST',
 body: JSON.stringify({ userId: user?.id })
 });
 
 if (!response.ok) throw new Error(`Erreur ${response.status}`);
 
 const data = await response.json();
 
 // Notification succès
 const notification = document.createElement('div');
 notification.className = 'fixed top-4 right-4 z-50 bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-6 py-3 rounded-lg shadow-xl transition-all duration-300';
 const container = document.createElement('div');
 container.className = 'flex items-center space-x-3';
 const iconWrapper = document.createElement('div');
 iconWrapper.className = 'w-6 h-6 bg-white/20 rounded-full flex items-center justify-center';
 const iconInner = document.createElement('div');
 iconInner.className = 'w-3 h-3 bg-white rounded-full';
 iconWrapper.appendChild(iconInner);
 const textWrapper = document.createElement('div');
 const title = document.createElement('div');
 title.className = 'font-bold';
 title.textContent = 'Collision synchronisée !';
 const subtitle = document.createElement('div');
 subtitle.className = 'text-sm opacity-90';
 subtitle.textContent = 'Consultez votre workspace Notion';
 textWrapper.appendChild(title);
 textWrapper.appendChild(subtitle);
 container.appendChild(iconWrapper);
 container.appendChild(textWrapper);
 notification.appendChild(container);
 document.body.appendChild(notification);
 setTimeout(() => notification.remove(), 4000);
 
 // Recharger pour mettre à jour les infos Notion
 await loadCollisions();
 } catch (error) {
 logger.error('Erreur sync Notion:', error);
 
 // Notification erreur
 const notification = document.createElement('div');
 notification.className = 'fixed top-4 right-4 z-50 bg-red-500 text-white px-6 py-3 rounded-lg shadow-xl';
 const errorTitle = document.createElement('div');
 errorTitle.className = 'font-bold';
 errorTitle.textContent = 'Erreur Notion';
 const errorText = document.createElement('div');
 errorText.className = 'text-sm';
 errorText.textContent = 'Vérifiez votre connexion Notion';
 notification.appendChild(errorTitle);
 notification.appendChild(errorText);
 document.body.appendChild(notification);
 setTimeout(() => notification.remove(), 5000);
 } finally {
 setSyncingNotion(prev => ({ ...prev, [collisionId]: false }));
 }
 };
 
 const getFilteredCollisions = () => {
 if (collisionsFilter === 'all') return patternCollisions;
 if (collisionsFilter === 'active') return patternCollisions.filter(c => !c.is_resolved);
 if (collisionsFilter === 'resolved') return patternCollisions.filter(c => c.is_resolved);
 return patternCollisions;
 };
 
 // ============================================================================
 // INNOVATION 5: DNA Score Functions
 // ============================================================================
 
 const loadMaturityScore = async () => {
 if (!user?.id) return;
 
 setScoreLoading(true);
 try {
 // SÉCURITÉ: JWT automatique via authFetch
 const response = await authFetch(`${API_URL}/api/maturity/score/${user.id}`);
 if (!response.ok) throw new Error(`Erreur ${response.status}`);
 
 const data = await response.json();
 setMaturityScore(data.score ? data : null);
 } catch (error) {
 logger.error('Erreur chargement DNA Score:', error);
 } finally {
 setScoreLoading(false);
 }
 };
 
 const calculateMaturityScore = async () => {
 if (!user?.id) return;
 
 // RESTRICTION FREE: Pas d'accès aux analyses Memory Pattern
 if (subscriptionTier === 'free') {
 setShowUpgradeModalMemory(true);
 return;
 }
 
 setScoreLoading(true);
 try {
 // SÉCURITÉ: JWT automatique via authFetch
 const response = await authFetch(`${API_URL}/api/maturity/calculate`, {
 method: 'POST',
 body: JSON.stringify({ userId: user.id })
 });
 
 if (!response.ok) throw new Error(`Erreur ${response.status}`);
 
 const data = await response.json();
 await loadMaturityScore();
 } catch (error) {
 logger.error('Erreur calcul DNA Score:', error);
 } finally {
 setScoreLoading(false);
 }
 };
 
 // ============================================================================
 // OAuth Notion Functions
 // ============================================================================
 
 const checkNotionConnection = async () => {
 if (!user?.id) return;
 
 try {
 // SÉCURITÉ: Utiliser authFetch pour JWT
 const response = await authFetch(`${API_URL}/api/integrations/notion/status/${user.id}`);
 const data = await response.json();
 setNotionIntegration(data.connected ? data.integration : null);
 } catch (error) {
 logger.error('Erreur vérification Notion:', error);
 }
 };
 
 const connectNotion = async () => {
 if (!user?.id) return;
 
 try {
 // SÉCURITÉ: Utiliser authFetch pour JWT
 const response = await authFetch(`${API_URL}/api/integrations/notion/auth-url?userId=${user.id}`);
 const { authUrl } = await response.json();
 // SÉCURITÉ #27: Ajouter noopener pour éviter accès window.opener
 window.open(authUrl, '_blank', 'width=600,height=800,noopener');
 
 // Polling pour détecter quand la connexion est établie
 const checkInterval = setInterval(async () => {
 await checkNotionConnection();
 if (notionIntegration) {
 clearInterval(checkInterval);
 }
 }, 2000);
 
 // Stop polling après 2 minutes
 setTimeout(() => clearInterval(checkInterval), 120000);
 } catch (error) {
 logger.error('Erreur connexion Notion:', error);
 }
 };
 
 const disconnectNotion = async () => {
 if (!user?.id) return;
 
 try {
 // SÉCURITÉ: Utiliser authFetch pour JWT
 const response = await authFetch(`${API_URL}/api/integrations/notion/disconnect/${user.id}`, {
 method: 'DELETE'
 });
 
 if (response.ok) {
 setNotionIntegration(null);
 }
 } catch (error) {
 logger.error('Erreur déconnexion Notion:', error);
 }
 };

 // Fonction pour restaurer une session complète (chat + insights + deep report + data viz)
 const restoreSessionComplete = async (sessionId: string) => {
 try {
 if (!user?.id) {
 logger.warn('[Session] No user, cannot restore session');
 return null;
 }

 // UTILISER DATAMANAGER - Cache-first avec memory + localStorage
 logger.log(`[Session] Restoring session ${sessionId} via DataManager...`);
 const fetchStart = Date.now();
 
 // DataManager gère TOUT: Memory cache → background refresh automatique
 const {
 insights,
 reports,
 dataViz,
 stats,
 session
 } = await DataManager.getSessionComplete(sessionId, user.id);
 
 logger.log(`[Session] Données chargées en ${Date.now() - fetchStart}ms`);
 
 
 // SYNCHRONISATION INSTANTANÉE DE TOUS LES ONGLETS
 
 // 1. Insights
 setConsoleInsights(insights || []);
 
 // 2. Deep Reports - Charger le content séparément (trop lourd)
 if (reports && reports.length > 0) {
 const latestReport = reports[0];
 setCurrentDeepReport(latestReport);
 setSessionDeepReports(reports);
 setHasNewDeepReport(true);
 
 // Charger le content en background si nécessaire
 if (!latestReport.content && latestReport.id) {
 authFetch(`${API_URL}/api/session/${sessionId}/deep-report/latest`)
 .then(res => res.ok ? res.json() : null)
 .then(data => {
 if (data?.report?.content && activeSessionId === sessionId) {
 setMarkdownContent(data.report.content);
 setCurrentDeepReport(data.report);
 }
 })
 .catch(err => logger.warn('Erreur chargement content:', err));
 } else {
 setMarkdownContent(latestReport.content || '');
 }
 } else {
 setCurrentDeepReport(null);
 setSessionDeepReports([]);
 setHasNewDeepReport(false);
 setMarkdownContent('# Rapport d\'analyse\n\nVos insights générés par l\'IA apparaîtront ici pour édition...');
 }
 
 // 3. Data Viz - Combiner dataViz API + insights avec chart_config
 const allCharts: any[] = [];
 
 // 3a. Charts depuis dataViz API
 if (dataViz && dataViz.length > 0) {
 const vizCharts = dataViz.map((viz: any) => ({
 id: viz.id,
 type: viz.chart_type,
 title: viz.title,
 data: viz.chart_config?.data || viz.chart_config,
 options: viz.chart_config?.options || {},
 insight: `Graphique généré depuis session ${sessionId}`,
 source: {
 fileName: `Session: ${sessionId}`,
 createdAt: new Date(viz.created_at)
 },
 interactive: true
 }));
 allCharts.push(...vizCharts);
 }
 
 // 3b. Charts depuis insights avec chart_config
 if (insights && insights.length > 0) {
 insights.forEach((insight: any) => {
 if (insight.chart_spec?.data || insight.chart_config?.data) {
 const chartSpec = insight.chart_spec || insight.chart_config;
 allCharts.push({
 id: insight.id,
 type: chartSpec.type || 'bar',
 title: insight.title,
 data: chartSpec.data,
 options: chartSpec.options || {},
 insight: insight.description,
 source: {
 analysisId: insight.id,
 fileName: `Session: ${sessionId}`,
 createdAt: new Date(insight.created_at)
 }
 });
 }
 });
 }
 
 setAiGeneratedCharts(allCharts);

 // 4. SYNCHRONISATION FINALE - Marquer la session comme active
 setActiveSessionId(sessionId);
 
 // PERSISTER LA SESSION ACTIVE
 localStorage.setItem('clairify_last_session_id', sessionId);
 
 logger.log(`[Session] Restaurée (${insights?.length || 0} insights, ${reports?.length || 0} reports, ${allCharts.length} charts)`);
 
 return {
 insights,
 reports,
 dataViz,
 stats
 };
 
 } catch (error) {
 logger.error('[Session] Erreur restauration:', error);
 return null;
 }
 };

 // INLINE DELETE - Pas de modal
 const handleDeleteSession = async (sessionId: string, sessionTitle: string) => {
 // Confirm avant suppression
 if (!confirm(`Supprimer "${sessionTitle}" ?\n\nTous les insights, rapports et data viz seront définitivement supprimés.`)) {
 return;
 }
 
 try {
 const response = await authFetch(`${API_URL}/api/chat/sessions/${sessionId}`, {
 method: 'DELETE'
 });
 
 if (!response.ok) {
 const error = await response.json();
 throw new Error(error.error || 'Erreur lors de la suppression');
 }
 
 // Supprimer de la liste locale
 setChatSessions(prev => prev.filter(s => s.id !== sessionId));
 
 // Si c'était la session active, réinitialiser
 if (activeSessionId === sessionId) {
 setActiveSessionId('');
 setConsoleInsights([]);
 setCurrentDeepReport(null);
 }
 
 logger.log('[Session] Supprimée:', sessionId);
 
 } catch (error: any) {
 logger.error('[Session] Erreur suppression:', error);
 alert(`Erreur: ${error.message || 'Impossible de supprimer la session'}`);
 }
 };

 // INLINE RENAME - Pas de modal
 const handleRenameSession = async (sessionId: string, newTitle: string) => {
 if (!newTitle.trim()) {
 alert('Le titre ne peut pas être vide');
 return;
 }
 
 try {
 const response = await authFetch(`${API_URL}/api/chat/sessions/${sessionId}`, {
 method: 'PATCH',
 headers: {
 'Content-Type': 'application/json'
 },
 body: JSON.stringify({
 title: newTitle.trim()
 })
 });
 
 if (!response.ok) {
 const error = await response.json();
 throw new Error(error.error || 'Erreur lors du renommage');
 }
 
 const { session } = await response.json();
 
 // Mettre à jour la liste locale
 setChatSessions(prev => prev.map(s => 
 s.id === sessionId 
 ? { ...s, title: session.title, name: session.title }
 : s
 ));
 
 // Quitter le mode édition
 setEditingSessionId(null);
 setEditingTitle('');
 
 logger.log('[Session] Renommée:', sessionId);
 
 } catch (error: any) {
 logger.error('[Session] Erreur renommage:', error);
 alert(`Erreur: ${error.message || 'Impossible de renommer la session'}`);
 }
 };

 // Fonction pour restaurer une analyse (insights + deep report + data viz)
 const restoreSessionAnalysis = async (sessionId: string) => {
 try {
 
 // 1. Charger les insights de la session
 const insightsResponse = await authFetch(`${API_URL}/api/insights/load/${sessionId}`);
 if (insightsResponse.ok) {
 const { insights } = await insightsResponse.json();
 
 // Transformer les insights pour l'interface
 const transformedInsights = insights.map((insight: any) => ({
 id: insight.id || `insight-${Date.now()}`,
 analysis_job_id: insight.analysis_job_id || sessionId,
 user_id: insight.user_id || 'current-user',
 title: insight.title || 'Insight Restauré',
 description: insight.description || insight.content || '',
 confidence_score: insight.confidence_score || 85,
 type: insight.type || 'recommendation',
 actionable_recommendations: insight.actionable_recommendations || [insight.description?.split('.')[0] + '.' || ''],
 is_pinned: insight.is_pinned || false,
 created_at: insight.created_at || new Date().toISOString()
 }));
 
 setConsoleInsights(transformedInsights);
 
 // Aller à l'onglet insights
 switchToTab('insights');
 }

 // 2. Charger les messages pour extraire les données d'analyse
 // SÉCURITÉ: Utiliser authFetch pour JWT
 const messagesResponse = await authFetch(`${API_URL}/api/chat/history/${sessionId}`);
 if (messagesResponse.ok) {
 const { messages } = await messagesResponse.json();
 
 // Rechercher les analyses dans les messages
 for (const message of messages) {
 if (message.role === 'assistant' && message.content) {
 if (message.content.includes('INSIGHT ACTIONNABLE') || 
 message.content.includes('SPECIFICATION JSON') ||
 message.content.includes('## ')) {
 
 const analysisData = {
 content: message.content,
 model: 'claude-haiku',
 sessionId: sessionId
 };
 
 // Synchroniser avec les insights
 handleInsightsGenerated(analysisData);
 
 break;
 }
 }
 }
 }

 // 3. TODO: Charger le deep report associé
 // 4. TODO: Synchroniser Data Viz avec les données de cette session
 
 } catch (error) {
 logger.error('Erreur restauration analyse:', error);
 }
 };

 // SÉCURITÉ CRITIQUE: Réinitialiser TOUS les states quand l'utilisateur change
 useEffect(() => {
 if (!user?.id) {
 // Nettoyer TOUT quand l'utilisateur se déconnecte
 setBusinessPatterns([]);
 setPatternCollisions([]);
 setMaturityScore(null);
 setChatSessions([]);
 setConsoleInsights([]);
 setMarkdownContent('');
 setAiGeneratedCharts([]);
 
 // Nettoyer le cache
 localStorage.removeItem(SESSIONS_CACHE_KEY);
 loadingSessionsRef.current = false;
 sessionsLoadedRef.current = false;
 
 logger.warn('[SÉCURITÉ] States + cache réinitialisés - utilisateur déconnecté');
 }
 }, [user?.id]);

 // FIX RACE CONDITION CRITIQUE: Attendre que user ET profile soient chargés avant de charger les données
 useEffect(() => {
 if (process.env.NODE_ENV === 'development') {
 logger.log('[Studio] useEffect triggered:', {
 isReady,
 hasUser: !!user?.id,
 hasProfile: !!profile,
 loading,
 sessionsLoaded: sessionsLoadedRef.current,
 timestamp: new Date().toISOString()
 });
 }
 
 // PROTECTION: Ne charger QUE si user ET profile sont prêts
 // Ceci élimine la race condition où loadChatSessions() était appelé avec profile = null
 if (!isReady || !user?.id || !profile) {
 logger.log('[Studio] En attente de user + profile...');
 return;
 }
 
 // OPTIMISATION: Charger le cache IMMÉDIATEMENT avant le guard
 // Ceci donne une UI instantanée même si l'API est lente
 if (!sessionsLoadedRef.current) {
 try {
 const cached = localStorage.getItem(SESSIONS_CACHE_KEY);
 if (cached) {
 const { data, timestamp, userId } = JSON.parse(cached);
 if (userId === user.id && data && data.length > 0) {
 const age = Date.now() - timestamp;
 logger.log(`[Studio] Affichage cache instantané (âge: ${Math.round(age/1000)}s)`);
 setChatSessions(data);
 // Ne pas marquer comme loaded pour permettre le refresh API
 }
 }
 } catch (error) {
 logger.warn('Erreur lecture cache initial:', error);
 }
 }
 
 // GUARD: Si déjà chargé depuis l'API, ne pas recharger (évite les multiples appels en dev mode)
 if (sessionsLoadedRef.current) {
 logger.log('[Studio] Sessions déjà chargées depuis API, skip');
 return;
 }
 
 logger.log('[Studio] User + Profile prêts, chargement des données...');
 
 // CHARGEMENT PARALLÈLE VIA DATAMANAGER
 Promise.all([
 loadChatSessions(),
 loadBusinessPatterns(),
 loadCollisions(),
 loadMaturityScore(),
 checkNotionConnection()
 ]).then(async ([loadedSessions]) => {
 // AUTO-CHARGEMENT DE LA DERNIÈRE SESSION - ÉVITER DASHBOARD VIDE
 
 // 1. Priorité: Récupérer la dernière session depuis localStorage
 const lastSessionId = localStorage.getItem('clairify_last_session_id');
 
 if (lastSessionId) {
 try {
 logger.log('[Studio] Auto-restore last session:', lastSessionId);
 await restoreSessionComplete(lastSessionId);
 switchToTab('insights'); // TOUJOURS ALLER SUR INSIGHTS
 return;
 } catch (error) {
 logger.warn('[Studio] Last session restore failed:', error);
 }
 }
 
 // 2. Fallback: Charger la session la plus récente (loadedSessions retourné par loadChatSessions)
 if (loadedSessions && loadedSessions.length > 0) {
 const latestSession = loadedSessions[0]; // Première = plus récente
 logger.log('[Studio] Auto-loading latest session:', latestSession.id);
 
 try {
 await restoreSessionComplete(latestSession.id);
 switchToTab('insights'); // TOUJOURS ALLER SUR INSIGHTS
 localStorage.setItem('clairify_last_session_id', latestSession.id);
 } catch (error) {
 logger.warn('[Studio] Latest session restore failed:', error);
 }
 } else {
 logger.log('[Studio] No sessions found - dashboard vide OK (user peut créer première session)');
 }
 }).catch(err => logger.warn('[Studio] Error loading initial data:', err));
 }, [isReady, user?.id, profile]) // Dépendance sur isReady + user + profile pour garantir l'ordre

 // =====================================================
 // REALTIME SUBSCRIPTIONS (via RealtimeManager)
 // =====================================================
 useEffect(() => {
 if (!user?.id) return;

 logger.log('[Studio] Setting up Realtime subscriptions...');

 // Subscribe to session changes (create, update, delete)
 const unsubSessions = RealtimeManager.subscribeSessions(user.id, (payload) => {
 logger.log('[Realtime] Session change:', payload.eventType, payload.new);

 if (payload.eventType === 'INSERT' && payload.new) {
 handleChatSessionCreated(payload.new);
 } else if (payload.eventType === 'DELETE' && payload.old) {
 setChatSessions(prev => prev.filter(s => s.id !== payload.old.id));
 } else if (payload.eventType === 'UPDATE' && payload.new) {
 setChatSessions(prev => prev.map(s => s.id === payload.new.id ? { ...s, ...payload.new } : s));
 }
 });

 // Subscribe to message changes (for active session)
 let unsubMessages: (() => void) | null = null;
 if (activeSessionId) {
 unsubMessages = RealtimeManager.subscribeMessages(activeSessionId, (payload) => {
 logger.log('[Realtime] Message change:', payload.eventType);

 if (payload.eventType === 'INSERT' && payload.new) {
 // Ajouter message à la session active
 // Note: FluidChat gère déjà les messages, mais on peut notifier d'autres composants
 logger.log('[Realtime] New message received');
 }
 });
 }

 return () => {
 unsubSessions?.();
 unsubMessages?.();
 };
 }, [user?.id, activeSessionId]);

 // =====================================================
 // HANDLERS POUR SYNCHRONISATION
 // =====================================================
 
 const handleChatSessionCreated = (session: any) => {
 setChatSessions(prev => {
 const existingIndex = prev.findIndex(s => s.id === session.id);
 if (existingIndex >= 0) {
 // Update existing session
 const updated = [...prev];
 updated[existingIndex] = { ...session, active: true };
 return updated;
 } else {
 // Add new session
 return [{ ...session, active: true }, ...prev.map(s => ({ ...s, active: false }))];
 }
 });
 };

 const handleInsightsGenerated = async (analysisData: any) => {
 
 if (analysisData && analysisData.content) {
 const content = analysisData.content;
 
 // FILTRE RENFORCÉ : Vérifier si c'est vraiment une analyse avec des données
 // Ignorer les messages simples, réponses basiques du chat, etc.
 
 // 1. Messages trop courts (moins de 100 caractères = probablement pas une vraie analyse)
 if (content.trim().length < 100) {
 logger.log('[Insights] Message trop court ignoré:', content.substring(0, 50));
 return;
 }
 
 // 2. Salutations et messages de bienvenue
 const isSimpleGreeting = /^(hello|hi|bonjour|salut|hey|bienvenue|welcome)[\s!.]*$/i.test(content.trim());
 if (isSimpleGreeting) {
 logger.log('[Insights] Salutation ignorée');
 return;
 }
 
 // 3. Phrases typiques du chat assistant sans analyse
 const isChatResponse = /^(je (suis|peux|vais)|comment puis-je|que puis-je|voulez-vous|souhaitez-vous|n'hésitez pas|voici|bienvenue dans|pour (commencer|démarrer))/i.test(content.trim());
 if (isChatResponse) {
 logger.log('[Insights] Réponse chat basique ignorée');
 return;
 }
 
 // 4. Vérifier la présence d'indicateurs de vraie analyse
 const hasDataIndicators = /(?:chiffre|données|analyse|€|%|métrique|insight|tableau|csv|excel|stratégie|opportunité|risque|recommandation|performance|croissance|budget|revenu|coût|marge|client|marché)/i.test(content);
 const hasInsightKeywords = content.includes('INSIGHT ACTIONNABLE') || content.includes('MÉTRIQUE') || content.includes('RECOMMANDATION');
 const hasStructuredAnalysis = /(?:###|##|\*\*INSIGHT|\*\*MÉTRIQUE|\*\*RECOMMANDATION)/i.test(content);
 
 // 5. N'accepter QUE les messages avec de vrais indicateurs d'analyse
 if (!hasDataIndicators && !hasInsightKeywords && !hasStructuredAnalysis) {
 logger.log('[Insights] Pas d\'indicateurs d\'analyse trouvés');
 return;
 }
 
 logger.log('[Insights] Message qualifié pour extraction d\'insights');
 
 // Extraire les insights actionnables du contenu
 const sessionId = analysisData.sessionId || activeSessionId;
 const insights: Insight[] = [];
 
 if (!sessionId) {
 logger.error('Aucun sessionId disponible pour sauvegarder les insights !');
 return; // Ne pas traiter si pas de session
 }
 
 // Rechercher les INSIGHTS ACTIONNABLES dans le texte
 const insightMatches = content.match(/\*\*INSIGHT ACTIONNABLE[^:]*:\*\*([^*]+)/g) || [];
 
 insightMatches.forEach((match: string, index: number) => {
 const insightText = match.replace(/\*\*INSIGHT ACTIONNABLE[^:]*:\*\*/, '').trim();
 const firstSentence = insightText.split('.')[0] + '.';
 
 insights.push({
 id: `insight-${Date.now()}-${index}`,
 analysis_job_id: sessionId,
 user_id: user?.id || 'current-user',
 title: `Insight Stratégique ${index + 1}`,
 description: insightText,
 confidence_score: 90 + Math.floor(Math.random() * 10), // 90-99%
 type: 'recommendation',
 actionable_recommendations: [firstSentence],
 is_pinned: false,
 created_at: new Date().toISOString()
 });
 });
 
 // Rechercher les métriques importantes
 const metriques = content.match(/[0-9,]+[€%]/g) || [];
 if (metriques.length > 0) {
 insights.push({
 id: `metrics-${Date.now()}`,
 analysis_job_id: sessionId,
 user_id: user?.id || '',
 title: 'Métriques Clés Détectées',
 description: `Métriques identifiées: ${metriques.slice(0, 5).join(', ')}`,
 confidence_score: 95,
 type: 'summary',
 actionable_recommendations: [`${metriques.length} métriques analysées`],
 is_pinned: false,
 created_at: new Date().toISOString()
 });
 }
 
 // NE PAS créer d'insight générique si aucun insight spécifique n'a été trouvé
 // Ceci évite les insights vides ou non pertinents dans l'historique
 if (insights.length === 0) {
 logger.log('[Insights] Aucun insight actionnable extrait, pas de sauvegarde');
 return; // Ne rien sauvegarder si pas d'insights pertinents
 }
 
 logger.log(`[Insights] ${insights.length} insight(s) extrait(s), sauvegarde...`);
 
 // Sauvegarder automatiquement les insights dans Supabase SEULEMENT si sessionId existe
 if (sessionId && insights.length > 0) {
 try {
 for (const insight of insights) {
 
 const payload = {
 sessionId: sessionId,
 insight: {
 ...insight,
 userId: user?.id // CORRECTION: Ajouter userId à l'insight
 }
 };
 
 
 // SÉCURITÉ: Utiliser authFetch pour JWT
 const response = await authFetch(`${API_URL}/api/insights/save`, {
 method: 'POST',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify(payload)
 });
 
 if (!response.ok) {
 logger.error('Erreur HTTP sauvegarde insight:', response.status, await response.text());
 }
 }
 } catch (error) {
 logger.error('Erreur sauvegarde insights:', error);
 }
 } else {
 logger.warn('Pas de sauvegarde: sessionId =', sessionId, ', insights =', insights.length);
 }
 
 setConsoleInsights(insights);
 setCurrentTab('insights'); // Auto-switch vers insights
 
 // MISE À JOUR OPTIMISTE : Mettre à jour localement immédiatement
 if (sessionId) {
 setChatSessions(prev => prev.map(session => 
 session.id === sessionId 
 ? { ...session, insights_count: (session.insights_count || 0) + insights.length }
 : session
 ));
 
 // Recharger la liste des sessions depuis l'API pour synchroniser
 setTimeout(() => {
 loadChatSessions(true).catch(err => logger.error('Erreur reload sessions:', err));
 }, 1000); // Délai pour permettre la sauvegarde backend
 }
 
 // PERSISTER LA NOUVELLE ANALYSE
 if (sessionId) {
 localStorage.setItem('clairify_last_session_id', sessionId);
 localStorage.setItem('clairify_last_analysis', JSON.stringify({
 sessionId,
 insights,
 deepReport: null,
 timestamp: new Date().toISOString()
 }));
 }
 
 // Stocker pour Deep Report et Data Viz (legacy)
 localStorage.setItem('latest_clairify_analysis', JSON.stringify({
 content: content,
 insights: insights,
 timestamp: new Date().toISOString(),
 model: analysisData.model || 'claude-haiku'
 }));
 }
 };

 const handlePdfGenerated = (pdfUrl: string) => {
 setCurrentPdfUrl(pdfUrl);
 setPdfViewerOpen(true);
 
 // Auto-switch to deep-report tab
 switchToTab('deep-report');
 };

 const handleReportUpdate = (content: string) => {
 setMarkdownContent(prev => {
 // Avoid duplicate content
 if (prev.includes(content.substring(0, 50))) {
 return prev;
 }
 return prev + '\n\n' + content;
 });
 
 // Auto-switch to deep-report tab
 switchToTab('deep-report');
 };

 const handleDataVizUpdate = (charts: any[]) => {
 // Update data visualization with new charts
 // This could trigger a refresh of the data-viz tab
 if (charts.length > 0) {
 switchToTab('data-viz');
 }
 };

 useEffect(() => {
  if (user) {
    // loadInsights() REMOVED - insights now loaded per session via restoreSessionComplete()
    loadAnalysisJobs()
    loadFiles()
  }
 }, [user?.id])

 useEffect(() => {
 if (user) {

 // NOUVEAU: Vérifier connexion réseau
 const checkOnlineStatus = () => {
 const online = navigator.onLine;
 setIsOnline(online);
 if (!online) {
 logger.warn('[Network] Connexion perdue');
 } else {
 logger.log('[Network] Connexion restaurée');
 // Tenter récupération session si connexion revenue
 recoverSession().then(result => {
 if (result.success) {
 logger.log('[Recovery] Session récupérée automatiquement');
 loadChatSessions(true).catch(err => logger.error('Erreur reload après recovery:', err));
 } else {
 logger.warn('[Recovery] Impossible de récupérer session');
 }
 });
 }
 };

 // Écouter changements réseau
 window.addEventListener('online', checkOnlineStatus);
 window.addEventListener('offline', checkOnlineStatus);

 // NOUVEAU: Subscription temps réel pour les sessions
 const sessionsSubscription = subscribeToChatSessions(user.id, async (payload) => {
 logger.log('[Realtime] Changement session:', payload.eventType, payload.new?.id);

 // Mettre à jour lastSync
 setLastSync(new Date());

 // Recharger les sessions automatiquement
 try {
 await loadChatSessions(true);
 logger.log('[Realtime] Sessions synchronisées automatiquement');
 } catch (error) {
 logger.error('[Realtime] Erreur sync sessions:', error);
 }
 });

    // S'abonner aux nouveaux insights EN TEMPS RÉEL (via RealtimeManager)
    const insightsSubscription = RealtimeManager.subscribeInsights(user.id, async (payload) => {
      if (payload.eventType === 'INSERT') {
        const newInsight = payload.new;

        logger.log('[Realtime] Nouvel insight détecté:', newInsight.session_id);

 // 1. Mettre à jour la liste globale des insights
 setInsights(prev => {
 // Vérifier si l'insight existe déjà (éviter doublons)
 const alreadyExists = prev.some(i => i.id === newInsight.id);
 if (alreadyExists) {
 return prev;
 }
 return [newInsight, ...prev];
 });

 // 2. Si l'insight appartient à la session active, l'ajouter aussi à consoleInsights
 if (activeSessionId && newInsight.session_id === activeSessionId) {
 setConsoleInsights(prev => {
 // Vérifier si l'insight existe déjà (éviter doublons)
 const alreadyExists = prev.some(i => i.id === newInsight.id);
 if (alreadyExists) {
 return prev;
 }
 return [newInsight, ...prev];
 });
 }

 // 3. MISE À JOUR OPTIMISTE : Mettre à jour localement immédiatement
 setChatSessions(prev => {
 const sessionIndex = prev.findIndex(s => s.id === newInsight.session_id);
 if (sessionIndex >= 0) {
 // Session existe, mettre à jour le compteur
 const updated = [...prev];
 updated[sessionIndex] = {
 ...updated[sessionIndex],
 insights_count: (updated[sessionIndex].insights_count || 0) + 1
 };
 logger.log('[Subscription] Compteur mis à jour localement:', updated[sessionIndex].insights_count);
 return updated;
 } else {
 // Session n'existe pas encore dans la liste, recharger depuis l'API
 logger.log('[Subscription] Session non trouvée, rechargement depuis API...');
 setTimeout(() => {
 loadChatSessions(true).catch(err => logger.error('Erreur reload sessions:', err));
 }, 500);
 return prev;
 }
 });

 // Mettre à jour lastSync
 setLastSync(new Date());
 }
 });

    return () => {
      window.removeEventListener('online', checkOnlineStatus);
      window.removeEventListener('offline', checkOnlineStatus);
      sessionsSubscription.unsubscribe();
      insightsSubscription(); // RealtimeManager retourne une fonction unsubscribe
    };
 }
 }, [user?.id, activeSessionId, chatSessions])

  // loadInsights() SUPPRIMÉ - On utilise QUE les insights de la session active via restoreSessionComplete()

 // FONCTION SUPPRIMÉE - Logic moved to restoreSessionComplete for unified data fetching

 // Fonction pour changer de Deep Report
 const selectDeepReport = (index: number) => {
 if (sessionDeepReports[index]) {
 const selectedReport = sessionDeepReports[index]
 setCurrentDeepReport(selectedReport)
 setMarkdownContent(selectedReport.content)
 setSelectedDeepReportIndex(index)
 }
 }

 const loadAnalysisJobs = async () => {
 if (!user?.id) return
 try {
 const data = await getAnalysisJobs(user.id)
 setAnalysisJobs(data)
 
 // Trouver l'analyse en cours
 const runningJob = data.find(job => job.status === 'processing')
 setCurrentAnalysis(runningJob || null)
 } catch (error) {
 logger.error('Error loading analysis jobs:', error)
 }
 }

 const loadFiles = async () => {
 if (!user?.id) return
 try {
 const data = await getDataImports(user.id)
 setFiles(data)
 } catch (error) {
 logger.error('Error loading files:', error)
 }
 }

 const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
 const file = event.target.files?.[0]
 if (!file || !user) return

 try {
 // Validation fichier
 const { validateFile } = await import('../utils/fileValidation');
 const validation = await validateFile(file);
 
 if (!validation.valid) {
 alert(`${validation.error}`);
 event.target.value = '';
 return;
 }

 // Upload vers Scaleway avec processing
 const { uploadFileWithProcessing } = await import('../lib/storage');
 
 logger.log(`[Studio] Uploading file: ${file.name}`);
 
 const { fileKey, fileId, orgPath, dataPreview } = await uploadFileWithProcessing(
 file,
 user.id,
 undefined // Pas de sessionId pour upload Studio global
 );

 logger.log(`[Studio] Fichier uploadé - Key: ${fileKey}, ID: ${fileId}, Org: ${orgPath}`);
 
 // Afficher notification succès
 alert(`Fichier "${file.name}" uploadé avec succès!\n\nOrganisation: ${orgPath}\nTaille: ${Math.round(file.size / 1024)} KB`);
 
 // TODO: Refresh file list ou afficher dans UI
 
 } catch (error) {
 logger.error('[Studio] Erreur upload:', error);
 
 if (error instanceof Error) {
 if (error.message.includes('Tokens insuffisants')) {
 alert(`${error.message}\n\nVeuillez recharger vos tokens pour continuer.`);
 } else {
 alert(`Erreur upload: ${error.message}`);
 }
 } else {
 alert('Erreur lors de l\'upload du fichier');
 }
 } finally {
 // Reset input
 event.target.value = '';
 }
 }

 useEffect(() => {
 if (process.env.NODE_ENV === 'development') {
 }
 }, [user, profile, loading]);

 // MEMORY LAYER: Charger les patterns au montage
 useEffect(() => {
 if (user?.id) {
 loadBusinessPatterns();
 }
 }, [user?.id]);

 // Redirection vers login si non connecté (DOIT être AVANT les returns conditionnels)
 useEffect(() => {
 logger.log('[Studio Auth Check]', {
 loading,
 user: user ? `${user.email} (${user.id})` : 'null',
 willRedirect: !loading && !user
 });

 if (!loading && !user) {
 const loginUrl = getLoginUrl();
 logger.log('[Studio] REDIRECT to login', {
 loginUrl,
 isAbsolute: loginUrl.startsWith('http')
 });
 
 if (loginUrl.startsWith('http')) {
 window.location.href = loginUrl;
 } else {
 navigate(loginUrl);
 }
 }
 }, [loading, user, navigate]);

 // Afficher le loader SEULEMENT si auth loading ET pas encore de sessions chargées
 if (loading && chatSessions.length === 0) {
 if (process.env.NODE_ENV === 'development') {
 }
 return (
 <div className="min-h-screen bg-theme-base flex items-center justify-center">
 <div className="text-center">
 <CreationSpark variant="mark" className="w-12 h-12 mx-auto mb-6 spark-core" />
 <p className="text-theme-secondary text-sm tracking-widest uppercase">Chargement du studio</p>
 </div>
 </div>
 )
 }

 if (!user || loading) {
 return (
 <div className="min-h-screen bg-theme-base flex items-center justify-center">
 <div className="text-center">
 <div className="w-8 h-8 border-2 border-accent-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
 <p className="text-theme-secondary">Vérification de l'authentification...</p>
 </div>
 </div>
 );
 }


 if (process.env.NODE_ENV === 'development') {
 }

// ✅ Big Data: maintenant disponible en PRODUCTION (V8.0.0)
const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

const tabs: Tab[] = [
  { id: 'insights', label: 'Insights', icon: Brain },
  { id: 'deep-report', label: 'Rapports', icon: FileText },
  { id: 'data-viz', label: 'Visualisations', icon: BarChart3 },
  { id: 'memory-patterns', label: 'Mémoire', icon: Sparkles },
  { id: 'big-data', label: 'Big Data', icon: Server },
]

 const generateBusinessCharts = () => {
 // Générer des graphiques basés sur les données réelles d'analyse
 // Si aucun insight disponible, retourner des graphiques vides ou basés sur les fichiers uploadés
 
 if (consoleInsights.length === 0 && files.length === 0) {
 return [
 {
 type: 'bar' as const,
 title: 'Données d\'analyse',
 data: {
 labels: ['Aucune donnée'],
 values: [0]
 },
 insight: 'Aucune donnée disponible. Uploadez des fichiers et générez des insights pour voir les visualisations.'
 }
 ];
 }

 const charts: any[] = [];

 // Graphiques basés sur les insights générés
 if (consoleInsights.length > 0) {

 // Répartition par type d'insight
 const insightTypes = consoleInsights.reduce((acc: any, insight: any) => {
 const type = insight.type || 'Analyse';
 acc[type] = (acc[type] || 0) + 1;
 return acc;
 }, {});

 charts.push({
 type: 'pie' as const,
 title: 'Répartition des insights par type',
 data: {
 labels: Object.keys(insightTypes),
 values: Object.values(insightTypes) as number[]
 },
 insight: `Répartition des ${consoleInsights.length} insights par catégorie d'analyse`
 });
 }

 // Graphiques basés sur les fichiers importés
 if (files.length > 0) {
 charts.push({
 type: 'line' as const,
 title: 'Évolution des imports de données',
 data: {
 labels: files.slice(0, 12).map(file => new Date(file.created_at).toLocaleDateString('fr-FR')),
 values: files.slice(0, 12).map(file => file.rows_count || 0)
 },
 insight: `${files.length} fichiers importés avec un total de ${files.reduce((acc, file) => acc + (file.rows_count || 0), 0)} lignes de données`
 });
 }

 return charts.length > 0 ? charts : [
 {
 type: 'bar' as const,
 title: 'Données d\'analyse',
 data: {
 labels: ['En attente'],
 values: [1]
 },
 insight: 'Prêt pour l\'analyse. Uploadez vos données pour commencer.'
 }
 ];
 }

 const renderTabContent = () => {
 switch (currentTab) {
 case 'insights':
 return (
 <InsightViewer 
 insights={consoleInsights} 
 onGenerateDeepReport={async (selectedInsights) => {
 
 const insightCount = selectedInsights.length;
 
 // WARNING si > 15 insights (mode chunking = plus cher)
 if (insightCount > 15) {
 const baseCost = 20000;
 const extraCost = (insightCount - 15) * 5000;
 const totalCost = baseCost + extraCost;
 
 const confirmed = window.confirm(
 `COÛT ÉLEVÉ\n\n` +
 `Vous avez sélectionné ${insightCount} insights.\n\n` +
 `Au-delà de 15 insights, le Deep Report utilise un mode avancé plus coûteux :\n\n` +
 `• Base (15 insights) : 20 000 tokens\n` +
 `• +${insightCount - 15} insights supplémentaires : +${extraCost.toLocaleString()} tokens\n\n` +
 `COÛT TOTAL : ${totalCost.toLocaleString()} tokens\n\n` +
 `Recommandation : Sélectionnez vos 10-15 insights les plus pertinents pour un coût optimal.\n\n` +
 `Continuer quand même ?`
 );
 
 if (!confirmed) {
 return;
 }
 }
 
 // VÉRIFICATION TOKENS : Coût fixe ≤15, progressif au-delà
 let requiredTokens;
 if (insightCount <= 15) {
 requiredTokens = 20000; // Coût fixe
 } else {
 requiredTokens = 20000 + ((insightCount - 15) * 5000); // Progressif
 }
 
 
 if (!hasEnoughTokens(requiredTokens)) {
 logger.warn('[Tokens] Insuffisants pour Deep Report');
 setRequiredTokensForDeepReport(requiredTokens);
 setSelectedInsightsForModal(insightCount);
 setShowInsufficientTokensModal(true);
 return; // Arrêter la génération
 }
 
 // Démarrer l'animation de génération
 setIsGeneratingDeepReport(true);
 setDeepReportProgress(0);
 
 // Auto-switch to deep-report tab
 switchToTab('deep-report');
 setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
 
 // PARSER automatiquement les graphiques du Deep Report
 setTimeout(() => {
 if (markdownContent) {
 const parsedCharts = parseSpecificationJSON(markdownContent);
 if (parsedCharts.length > 0) {
 setAiGeneratedCharts(prev => [...prev, ...parsedCharts]);
 }
 }
 }, 500);
 
 try {
 // Étapes réalistes avec appel IA
 // Pas d'animations factices - traitement direct
 
 // =====================================================
 // APPEL IA RÉVOLUTIONNAIRE POUR DEEP REPORT
 // =====================================================
 
 
 // Appel au nouvel endpoint Deep Report spécialisé
 // SÉCURITÉ: Utiliser authFetch pour JWT
 const response = await authFetch(`${API_URL}/api/deep-report/generate`, {
 method: 'POST',
 headers: {
 },
 body: JSON.stringify({
 insights: selectedInsights,
 title: `Deep Report Stratégique - ${new Date().toLocaleDateString('fr-FR')}`,
 sessionId: activeSessionId, // CRITIQUE : Pour sauvegarder dans la bonne session
 userId: user.id // CRITIQUE : Pour associer au bon utilisateur
 })
 });

 if (!response.ok) {
 throw new Error(`HTTP error! status: ${response.status}`);
 }

 const reader = response.body?.getReader();
 const decoder = new TextDecoder();
 let buffer = '';
 let aiGeneratedReport = '';
 let detectedCharts: any[] = [];

 setDeepReportProgress(10);

 while (true) {
 const { done, value } = await reader!.read();
 if (done) break;

 buffer += decoder.decode(value, { stream: true });
 const lines = buffer.split('\n');
 buffer = lines.pop() || '';

 for (const line of lines) {
 if (line.startsWith('data: ')) {
 try {
 const data = JSON.parse(line.slice(6));
 
 if (data.type === 'content_delta') {
 aiGeneratedReport = data.fullContent || '';
 detectedCharts = data.chartsDetected || [];
 
 // Mettre à jour en temps réel
 setMarkdownContent(aiGeneratedReport);
 setDeepReportProgress(Math.min(90, (aiGeneratedReport.length / 5000) * 90));
 }
 
 if (data.type === 'completed') {
 aiGeneratedReport = data.fullContent;
 detectedCharts = data.chartsDetected || [];
 setDeepReportProgress(100);
 break;
 }
 
 if (data.type === 'error') {
 throw new Error(data.error);
 }
 } catch (e) {
 logger.warn('Parse error:', e);
 }
 }
 }
 }

 // Fin du streaming - le rapport IA est maintenant complet
 
 // =====================================================
 // SAUVEGARDER DANS SUPABASE
 // =====================================================
 
 const reportData = {
 id: `deep-report-${Date.now()}`,
 title: `Deep Report Stratégique - ${new Date().toLocaleDateString('fr-FR')}`,
 content: aiGeneratedReport,
 insights_used: selectedInsights,
 generated_at: new Date().toISOString(),
 user_id: user?.id,
 charts_recommended: detectedCharts.length > 0 ? detectedCharts : [
 { type: 'trend', title: 'Évolution des KPIs', data: [] },
 { type: 'correlation', title: 'Matrice de Corrélations', data: [] },
 { type: 'impact', title: 'Impact Stratégique', data: [] }
 ]
 };
 
 // Sauvegarder le rapport dans Supabase
 try {
 const savedReportId = await saveDeepReportRPC(
 reportData.title,
 reportData.content,
 reportData.insights_used,
 reportData.charts_recommended,
 { generated_by: 'ai', user_id: user?.id },
 activeSessionId
 );
 
 if (savedReportId) {
 reportData.id = savedReportId;
 }
 } catch (supabaseError) {
 logger.warn('Supabase save failed, using localStorage fallback:', supabaseError);
 localStorage.setItem(`deep-report-${reportData.id}`, JSON.stringify(reportData));
 }
 
 // Stocker Deep Report pour téléchargement PDF
 setCurrentDeepReport(reportData);
 
 // =====================================================
 // GÉNÉRER GRAPHIQUES POUR DATA VIZ
 // =====================================================
 
 const autoGeneratedCharts = selectedInsights.map((insight, index) => ({
 type: insight.type === 'trend' ? 'line' : insight.type === 'correlation' ? 'scatter' : 'bar',
 title: `${insight.title} - Visualisation IA`,
 data: {
 labels: ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun'],
 values: Array.from({length: 6}, () => Math.floor(Math.random() * 100) + insight.confidence)
 },
 insight: `Graphique auto-généré par IA pour: ${insight.description}`,
 source: {
 fileName: `AI-Generated-${insight.type}`,
 createdAt: new Date()
 },
 interactive: true,
 }));
 
 // Synchroniser avec Data Viz
 setAiGeneratedCharts(autoGeneratedCharts);
 setCurrentDeepReport(reportData);
 
 // Finaliser
 setDeepReportProgress(100);
 
 // IMPORTANT: Mettre à jour le contenu Markdown avec le rapport généré
 setMarkdownContent(aiGeneratedReport);
 
 // Débloquer l'interface et switcher vers l'onglet Deep Report
 setIsGeneratingDeepReport(false);
 setHasNewDeepReport(true);
 
 // PERSISTER LE DEEP REPORT GÉNÉRÉ
 if (activeSessionId) {
 localStorage.setItem('clairify_last_analysis', JSON.stringify({
 sessionId: activeSessionId,
 insights: selectedInsights,
 deepReport: reportData,
 timestamp: new Date().toISOString()
 }));
 }
 
 // FORCER RECHARGEMENT COMPLET DES DONNÉES
 setTimeout(async () => {
 
 // Les insights seront rechargés automatiquement via restoreSessionComplete() si nécessaire
 // await loadInsights(); // REMOVED - no longer needed
 
 // Le Deep Report est déjà dans markdownContent, pas besoin de recharger
 
 // Switcher vers l'onglet Deep Report
 switchToTab('deep-report');
 }, 500);
 
 // SÉCURITÉ: Notification toast avec DOM API (pas innerHTML)
 setTimeout(() => {
 const notification = document.createElement('div');
 notification.className = 'fixed top-4 right-4 z-50 bg-gradient-to-r from-orange-600 to-purple-600 text-white px-6 py-3 rounded-lg shadow-xl transform transition-all duration-300 translate-x-full';
 
 const container = document.createElement('div');
 container.className = 'flex items-center space-x-3';
 
 const iconWrapper = document.createElement('div');
 iconWrapper.className = 'w-6 h-6 bg-theme-surface rounded-full flex items-center justify-center';
 const iconInner = document.createElement('div');
 iconInner.className = 'w-3 h-3 bg-blue-600 rounded-full animate-pulse';
 iconWrapper.appendChild(iconInner);
 
 const textWrapper = document.createElement('div');
 const title = document.createElement('div');
 title.className = 'font-bold';
 title.textContent = ' Deep Report IA Généré!';
 const subtitle = document.createElement('div');
 subtitle.className = 'text-sm opacity-90';
 subtitle.textContent = 'Graphiques synchronisés avec Data Viz';
 textWrapper.appendChild(title);
 textWrapper.appendChild(subtitle);
 
 container.appendChild(iconWrapper);
 container.appendChild(textWrapper);
 notification.appendChild(container);
 document.body.appendChild(notification);
 setTimeout(() => notification.classList.remove('translate-x-full'), 100);
 setTimeout(() => {
 notification.classList.add('translate-x-full');
 setTimeout(() => notification.remove(), 300);
 }, 5000);
 }, 500);
 
 
 } catch (error) {
 logger.error('AI Deep Report generation failed:', error);
 setIsGeneratingDeepReport(false);
 setDeepReportProgress(0);
 
 // Vérifier si c'est une erreur 429 (Rate Limit / Plan insuffisant)
 const errorMessage = error instanceof Error ? error.message : String(error);
 if (errorMessage.includes('429')) {
 // Modal élégant pour upgrade
 const upgradeChoice = window.confirm(
 `Fonctionnalité Premium\n\n` +
 `Les Deep Reports stratégiques sont réservés aux plans Pro, Business et Enterprise.\n\n` +
 `Avec un plan premium, vous débloquez :\n` +
 `• Deep Reports illimités générés par IA\n` +
 `• Pattern Detection & Memory avancée\n` +
 `• Export PDF professionnel\n` +
 `• Rollover de tokens (+20%)\n\n` +
 `Voulez-vous découvrir nos plans premium ?`
 );
 
 if (upgradeChoice) {
 window.location.href = '/pricing';
 }
 } else {
 // Autre erreur : afficher message générique
 alert(
 `Erreur lors de la génération du Deep Report\n\n` +
 `Une erreur technique est survenue. Veuillez réessayer dans quelques instants.\n\n` +
 `Si le problème persiste, contactez le support.`
 );
 }
 }
 }}
 onExportAllInsights={async () => {
 
 if (consoleInsights.length === 0) {
 alert('Aucun insight à exporter');
 return;
 }
 
 try {
 // EXPORT PDF NATIF DE TOUS LES INSIGHTS
 const pdf = new jsPDF('p', 'mm', 'a4');
 const pageWidth = 210;
 const pageHeight = 297;
 const margin = 20;
 const lineHeight = 7;
 const maxWidth = pageWidth - (margin * 2);
 
 // Couleurs harmonisées
 const colors = {
 title: [0, 0, 0] as [number, number, number],
 subtitle: [0, 100, 200] as [number, number, number],
 text: [0, 0, 0] as [number, number, number],
 meta: [100, 100, 100] as [number, number, number],
 accent: [150, 0, 150] as [number, number, number]
 };
 
 let yPosition = margin;
 
 // Traitement de tous les insights
 for (let i = 0; i < consoleInsights.length; i++) {
 const insight = consoleInsights[i];
 
 // Nouvelle page si nécessaire
 if (yPosition > pageHeight - margin - 50) {
 pdf.addPage();
 yPosition = margin;
 }
 
 // Titre de l'insight
 pdf.setFontSize(14);
 pdf.setTextColor(...colors.title);
 pdf.text(`${i + 1}. ${insight.title}`, margin, yPosition);
 yPosition += lineHeight * 1.5;
 
 // Description avec markdown
 const description = insight.description || 'Aucune description disponible';
 const lines = description.split('\n');
 
 for (const line of lines) {
 if (yPosition > pageHeight - margin - 20) {
 pdf.addPage();
 yPosition = margin;
 }
 
 if (line.startsWith('## ')) {
 pdf.setFontSize(12);
 pdf.setTextColor(...colors.subtitle);
 pdf.text(line.replace('## ', ''), margin, yPosition);
 yPosition += lineHeight * 1.3;
 } else if (line.startsWith('### ')) {
 pdf.setFontSize(11);
 pdf.setTextColor(...colors.accent);
 pdf.text(line.replace('### ', ''), margin, yPosition);
 yPosition += lineHeight * 1.2;
 } else if (line.includes('|') && (line.includes('---') || line.includes('--'))) {
 continue;
 } else if (line.includes('|') && line.trim().startsWith('|')) {
 // Tableau markdown
 const cells = line.split('|').map((cell: string) => cell.trim()).filter((cell: string) => cell !== '');
 const cellWidth = maxWidth / cells.length;
 let xPos = margin;
 
 for (const cell of cells) {
 pdf.setDrawColor(220, 220, 220);
 pdf.setLineWidth(0.2);
 pdf.rect(xPos, yPosition - 4, cellWidth, lineHeight + 2, 'S');
 
 pdf.setFontSize(8);
 pdf.setTextColor(...colors.text);
 const cellText = cell.length > 12 ? cell.substring(0, 10) + '...' : cell;
 pdf.text(cellText, xPos + 2, yPosition);
 xPos += cellWidth;
 }
 yPosition += lineHeight * 1.2;
 } else if (line.trim() !== '') {
 pdf.setFontSize(10);
 pdf.setTextColor(...colors.text);
 const processedLine = line.replace(/\*\*(.*?)\*\*/g, '$1');
 const splitText = pdf.splitTextToSize(processedLine, maxWidth);
 pdf.text(splitText, margin, yPosition);
 yPosition += lineHeight * splitText.length;
 } else {
 yPosition += lineHeight * 0.3;
 }
 }
 
 // Espacement entre insights
 yPosition += lineHeight * 1.5;
 
 // Ligne de séparation
 if (i < consoleInsights.length - 1) {
 pdf.setDrawColor(200, 200, 200);
 pdf.setLineWidth(0.3);
 pdf.line(margin, yPosition, pageWidth - margin, yPosition);
 yPosition += lineHeight;
 }
 }
 
 // Sauvegarde sans signature
 pdf.save(`Tous-Insights-${new Date().toISOString().slice(0, 10)}.pdf`);
 
 } catch (error) {
 logger.error('Erreur export PDF natif de tous les insights:', error);
 alert('Erreur lors de l\'export PDF de tous les insights');
 }
 }}
 onExportInsight={async (insight) => {
 
 try {
 // EXPORT PDF NATIF HARMONISÉ avec Deep Report
 const pdf = new jsPDF('p', 'mm', 'a4');
 const pageWidth = 210;
 const pageHeight = 297;
 const margin = 20;
 const lineHeight = 7;
 const maxWidth = pageWidth - (margin * 2);
 
 // Couleurs harmonisées avec Deep Report
 const colors = {
 title: [0, 0, 0] as [number, number, number], // Noir
 subtitle: [0, 100, 200] as [number, number, number], // Bleu
 text: [0, 0, 0] as [number, number, number], // Noir
 meta: [100, 100, 100] as [number, number, number], // Gris
 accent: [150, 0, 150] as [number, number, number] // Violet
 };
 
 let yPosition = margin;
 
 // Traitement de l'insight individuel avec markdown correct
 const description = insight.description || 'Aucune description disponible';
 const lines = description.split('\n');
 
 // Titre de l'insight
 pdf.setFontSize(16);
 pdf.setTextColor(...colors.title);
 pdf.text(insight.title, margin, yPosition);
 yPosition += lineHeight * 2;
 
 for (const line of lines) {
 // Vérifier si on doit ajouter une nouvelle page
 if (yPosition > pageHeight - margin - 20) {
 pdf.addPage();
 yPosition = margin;
 }
 
 if (line.startsWith('# ')) {
 // Titre principal
 pdf.setFontSize(14);
 pdf.setTextColor(...colors.title);
 const title = line.replace('# ', '');
 pdf.text(title, margin, yPosition);
 yPosition += lineHeight * 1.5;
 } else if (line.startsWith('## ')) {
 // Sous-titre
 pdf.setFontSize(12);
 pdf.setTextColor(...colors.subtitle);
 const subtitle = line.replace('## ', '');
 pdf.text(subtitle, margin, yPosition);
 yPosition += lineHeight * 1.3;
 } else if (line.startsWith('### ')) {
 // Sous-sous-titre
 pdf.setFontSize(11);
 pdf.setTextColor(...colors.accent);
 const subsubtitle = line.replace('### ', '');
 pdf.text(subsubtitle, margin, yPosition);
 yPosition += lineHeight * 1.2;
 } else if (line.startsWith('- ') || line.startsWith('* ')) {
 // Liste
 pdf.setFontSize(10);
 pdf.setTextColor(...colors.text);
 const text = '• ' + line.replace(/^[-*] /, '');
 const splitText = pdf.splitTextToSize(text, maxWidth - 10);
 pdf.text(splitText, margin + 5, yPosition);
 yPosition += lineHeight * splitText.length;
 } else if (line.includes('|') && (line.includes('---') || line.includes('--'))) {
 // Ligne de séparation de tableau - ignorer
 continue;
 } else if (line.includes('|') && line.trim().startsWith('|')) {
 // LIGNE DE TABLEAU MARKDOWN
 const cells = line.split('|').map(cell => cell.trim()).filter(cell => cell !== '');
 
 // Vérifier si on a assez d'espace pour le tableau
 if (yPosition > pageHeight - margin - 30) {
 pdf.addPage();
 yPosition = margin;
 }
 
 const cellWidth = maxWidth / cells.length;
 let xPos = margin;
 
 // Dessiner les cellules
 for (let i = 0; i < cells.length; i++) {
 const cell = cells[i];
 
 // Fond gris léger pour les en-têtes
 const isHeader = line === lines.find(l => l.includes('|') && l.trim().startsWith('|'));
 if (isHeader) {
 pdf.setFillColor(245, 245, 245);
 pdf.rect(xPos, yPosition - 4, cellWidth, lineHeight + 2, 'F');
 }
 
 // Bordures
 pdf.setDrawColor(220, 220, 220);
 pdf.setLineWidth(0.2);
 pdf.rect(xPos, yPosition - 4, cellWidth, lineHeight + 2, 'S');
 
 // Texte
 pdf.setFontSize(8);
 pdf.setTextColor(...(isHeader ? colors.title : colors.text));
 const cellText = cell.length > 15 ? cell.substring(0, 13) + '...' : cell;
 pdf.text(cellText, xPos + 2, yPosition);
 
 xPos += cellWidth;
 }
 yPosition += lineHeight * 1.2;
 } else if (line.trim() !== '') {
 // Texte normal avec gestion du markdown
 pdf.setFontSize(10);
 pdf.setTextColor(...colors.text);
 
 // Supprimer les ** pour éviter l'affichage brut
 let processedLine = line.replace(/\*\*(.*?)\*\*/g, '$1');
 
 const splitText = pdf.splitTextToSize(processedLine, maxWidth);
 pdf.text(splitText, margin, yPosition);
 yPosition += lineHeight * splitText.length;
 } else {
 // Ligne vide
 yPosition += lineHeight * 0.5;
 }
 }
 
 // Sauvegarde sans signature
 const filename = `Insight-${insight.title.replace(/[^a-zA-Z0-9]/g, '-')}-${new Date().toISOString().slice(0, 10)}.pdf`;
 pdf.save(filename);
 
 } catch (error) {
 logger.error('Erreur export PDF natif insights:', error);
 alert('Erreur lors de l\'export PDF natif des insights');
 }
 }}
 />
 )
 case 'deep-report':
 return (
 <div className="flex-1">
 <div className="max-w-6xl mx-auto">
 <div className="mb-10">
 <p className="page-kicker mb-3">Analyse stratégique</p>
 <h2 className="page-title">Rapports</h2>
 <p className="text-theme-secondary text-sm mt-2 max-w-lg">
 Document complet généré à partir de vos insights.
 </p>
 </div>
 
 {/* Report Content - PLEINE LARGEUR */}
 <div className="w-full">
 <div className="card-modern overflow-hidden h-full">
 {/* Header avec actions */}
 <div className="flex items-center justify-between p-4 border-b border-theme bg-theme-surface">
 <div className="flex items-center space-x-4">
 <h3 className="text-lg font-semibold text-theme-primary">Rapport d'analyse</h3>
 
 {/* Sélecteur discret pour les Deep Reports précédents */}
 {sessionDeepReports.length > 1 && (
 <div className="flex items-center space-x-2">
 <span className="text-xs text-theme-secondary">Rapport:</span>
 <select
 value={selectedDeepReportIndex}
 onChange={(e) => selectDeepReport(parseInt(e.target.value))}
 className="input-modern"
 >
 {sessionDeepReports.map((report, index) => (
 <option key={report.id} value={index}>
 {index === 0 ? 'Dernier' : `${index + 1}`} - {new Date(report.created_at).toLocaleDateString('fr-FR')}
 </option>
 ))}
 </select>
 </div>
 )}
 </div>
 <div className="flex items-center space-x-3">
 {hasNewDeepReport && (
 <div className="badge-success flex items-center space-x-2">
 <div className="w-2 h-2 bg-accent-primary rounded-full animate-pulse"></div>
 <span>Nouveau rapport</span>
 </div>
 )}
 <button
 onClick={() => {
 if (currentDeepReport?.id) {
 setShowPDFPreview(true);
 } else {
 alert('Générez d\'abord un Deep Report depuis l\'onglet Insights');
 }
 }}
 disabled={!currentDeepReport?.id}
 className={`btn-modern flex items-center space-x-2 ${
 currentDeepReport?.id
 ? 'btn-primary shadow-glow-green'
 : 'bg-theme-hover text-theme-disabled cursor-not-allowed opacity-50'
 }`}
 >
 <Download className="w-4 h-4" />
 <span>Télécharger</span>
 </button>
 </div>
 </div>
 
 {/* Contenu Markdown */}
 <div className="flex-1 p-6 overflow-y-auto">
 <div className="prose max-w-none">
 {markdownContent ? (
 <DeepReportRenderer 
 content={typeof markdownContent === 'string' 
 ? markdownContent 
 : typeof markdownContent === 'object' 
 ? JSON.stringify(markdownContent, null, 2)
 : String(markdownContent)
 }
 className="text-theme-primary"
 />
 ) : (
 <div className="text-center py-12">
 <h1 className="text-3xl font-bold text-theme-text-primary mb-4">Rapport d'analyse</h1>
 <p className="text-theme-text-secondary text-lg">Vos insights générés par l'IA apparaîtront ici pour édition...</p>
 </div>
 )}
 </div>
 </div>
 </div>
 </div>
 </div>
 </div>
 )
 case 'data-viz':
 return (
 <div className="p-6">
 <DataVisualization 
 charts={[...generateBusinessCharts(), ...aiGeneratedCharts]} 
 insightCharts={consoleInsights}
 onChartClick={(chart) => {
 // Nettoyer l'insight - TRANSFORMATION EN LISTES STRUCTURÉES
 const cleanInsight = (chart.insight || 'Graphique interactif généré à partir des données.')
 .split('\n')
 .filter(line => {
 const trimmed = line.trim();
 // Éliminer TOUTES les lignes de séparation (multiples patterns)
 if (!trimmed) return true; // Garder les lignes vides pour l'espacement
 if (trimmed.includes('|') && /[-=|]{3,}/.test(trimmed)) return false; // |---|---|
 if (/^[|\-\s=]+$/.test(trimmed)) return false; // Que des pipes/tirets
 if (trimmed.match(/^[\|\s]*[-=]{2,}[\|\s]*$/)) return false; // Variations
 return true;
 })
 .map((line, idx, arr) => {
 const trimmed = line.trim();
 
 // Ligne de tableau markdown - Conversion en liste élégante
 if (trimmed.includes('|') && trimmed.startsWith('|')) {
 const cells = trimmed.split('|')
 .map(cell => cell.trim())
 .filter(cell => cell !== '' && !/^[-=\s]+$/.test(cell));
 
 if (cells.length === 0) return '';
 
 // Premier tableau = headers, convertir en titre
 const prevLine = arr[idx - 1]?.trim() || '';
 const isHeader = !prevLine.includes('|') || idx === 0;
 
 if (isHeader) {
 return '\n**' + cells.join(' • ') + '**';
 }
 
 // Lignes de données - format compact avec bullets
 return cells.map(cell => ` • ${cell}`).join('\n');
 }
 
 return line;
 })
 .filter(line => line !== '') // Supprimer les lignes vides en trop
 .join('\n');
 
 setMarkdownContent(`# ${chart.title}\n\n## Description\n${cleanInsight}\n\n## Données\n${chart.data.labels.map((label, index) => `- **${label}**: ${chart.data.values[index]?.toLocaleString('fr-FR') || 'N/A'}`).join('\n')}\n\n## Source\n${chart.source?.fileName ? `- **Fichier**: ${chart.source.fileName}` : ''}\n${chart.source?.createdAt ? `- **Date**: ${chart.source.createdAt.toLocaleDateString('fr-FR')}` : ''}\n\n---\n*Graphique créé par Clairify AI • ${new Date().toLocaleDateString('fr-FR')}*`);
 switchToTab('deep-report');
 }}
 autoGenerate={true}
 onAutoGenerate={(newCharts) => {
 // Ajouter les graphiques auto-générés
 setAiGeneratedCharts(prev => [...prev, ...newCharts]);
 
 // PARSER les SPECIFICATION JSON du Deep Report actuel
 if (markdownContent && markdownContent.includes('SPECIFICATION JSON')) {
 const parsedCharts = parseSpecificationJSON(markdownContent);
 if (parsedCharts.length > 0) {
 setAiGeneratedCharts(prev => [...prev, ...parsedCharts]);
 }
 }
 
 }}
 onEmbeddedEdit={(chart) => {
 // TODO: Implement embedded editor
 }}
 />
 </div>
 )
 case 'memory-patterns':
 return (
 <div className="max-w-7xl mx-auto">
 <div className="mb-10">
 <div className="flex items-end justify-between gap-6 mb-6">
 <div className="flex-1">
 <p className="page-kicker mb-3">Intelligence persistante</p>
 <h2 className="page-title">Mémoire</h2>
 <p className="text-theme-secondary text-sm mt-2 max-w-xl">
 Plus vous analysez, plus Clairify reconnaît ce qui se répète.
 </p>
 
 {!canUseMemoryPatterns && (
 <div className="mt-5 px-4 py-3 border border-theme bg-theme-surface">
 <p className="text-sm text-theme-primary leading-relaxed">
 <span className="font-medium">Trois analyses minimum.</span>
 <span className="text-theme-secondary"> Effectuez au moins 3 analyses dans la console pour débloquer la mémoire. </span>
 <span className="font-mono text-xs">({sessionsWithInsights}/3)</span>
 </p>
 </div>
 )}
 </div>
 
 {/* Bouton dynamique selon le tab actif */}
 {memorySubTab === 'patterns' && (
 <button
 onClick={triggerPatternDetection}
 disabled={patternsLoading || !canUseMemoryPatterns}
 className={`btn-modern btn-primary ${
 (patternsLoading || !canUseMemoryPatterns) ? 'opacity-50 cursor-not-allowed' : ''
 }`}
 title={!canUseMemoryPatterns ? `Minimum 3 analyses requises (${sessionsWithInsights}/3)` : ''}
 >
 {patternsLoading ? (
 <>
 <div className="w-4 h-4 border-2 border-theme-inverse border-t-transparent rounded-full animate-spin" />
 <span>Analyse en cours...</span>
 </>
 ) : (
 <>
 <Sparkles className="w-5 h-5" />
 <span>Détecter nouveaux patterns</span>
 </>
 )}
 </button>
 )}

 {memorySubTab === 'dna' && (
 <button
 onClick={calculateMaturityScore}
 disabled={scoreLoading || !canUseMemoryPatterns}
 className={`btn-modern btn-primary ${
 (scoreLoading || !canUseMemoryPatterns) ? 'opacity-50 cursor-not-allowed' : ''
 }`}
 title={!canUseMemoryPatterns ? `Minimum 3 analyses requises (${sessionsWithInsights}/3)` : ''}
 >
 {scoreLoading ? (
 <>
 <div className="w-4 h-4 border-2 border-theme-inverse border-t-transparent rounded-full animate-spin" />
 <span>Calcul en cours...</span>
 </>
 ) : (
 <>
 <Activity className="w-5 h-5" />
 <span>Calculer DNA Score</span>
 </>
 )}
 </button>
 )}

{memorySubTab === 'collisions' && (
<button
onClick={detectCollisions}
disabled={collisionsLoading || !canUseMemoryPatterns}
className={`btn-modern ${
collisionsLoading || !canUseMemoryPatterns
? 'btn-secondary opacity-50 cursor-not-allowed'
: 'btn-primary'
}`}
title={!canUseMemoryPatterns ? `Minimum 3 analyses requises (${sessionsWithInsights}/3)` : ''}
>
{collisionsLoading ? (
<>
<div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
<span>Détection en cours...</span>
</>
) : (
<>
<AlertTriangle className="w-5 h-5" />
<span>Détecter collisions</span>
</>
)}
</button>
)}
 </div>
 </div>

 {/* Error State */}
 {patternsError && (
 <div className="bg-error border-error p-4 mb-6 rounded-xl">
 <p className="text-error-text text-sm font-medium">{patternsError}</p>
 </div>
 )}

 {/* Sub-navigation tabs - EN HAUT, BIEN VISIBLES */}
 <div className="mb-8">
 <div className="flex items-end gap-1 mb-8 border-b border-theme">
 <button
 onClick={() => setMemorySubTab('patterns')}
 className={`studio-tab ${memorySubTab === 'patterns' ? 'is-active' : ''}`}
 >
 Patterns
 </button>
 <button
 onClick={() => setMemorySubTab('dna')}
 className={`studio-tab ${memorySubTab === 'dna' ? 'is-active' : ''}`}
 >
 DNA Score
 </button>
 <button
 onClick={() => setMemorySubTab('collisions')}
 className={`studio-tab ${memorySubTab === 'collisions' ? 'is-active' : ''}`}
 >
 Collisions
 {patternCollisions.length > 0 && (
 <span className="ml-2 font-mono text-[10px]" style={{ color: 'var(--spark-gold)' }}>
 {patternCollisions.length}
 </span>
 )}
 </button>
 </div>

 <div className="mb-8">
 {memorySubTab === 'patterns' && (
 <div className="border-l-2 pl-4" style={{ borderColor: 'var(--spark-gold)' }}>
 <h4 className="text-sm font-medium text-theme-primary mb-1">Comportements récurrents</h4>
 <p className="text-sm text-theme-secondary leading-relaxed max-w-2xl">
 L'IA identifie ce qui se répète dans votre historique. Validez un pattern pour l'inscrire dans la mémoire.
 </p>
 </div>
 )}
 {memorySubTab === 'dna' && (
 <div className="border-l-2 pl-4" style={{ borderColor: 'var(--spark-gold)' }}>
 <h4 className="text-sm font-medium text-theme-primary mb-1">Maturité data-driven</h4>
 <p className="text-sm text-theme-secondary leading-relaxed max-w-2xl">
 Cinq dimensions : qualité des données, prédictibilité, risques, optimisation, automatisation.
 </p>
 </div>
 )}
 {memorySubTab === 'collisions' && (
 <div className="border-l-2 pl-4" style={{ borderColor: 'var(--spark-gold)' }}>
 <h4 className="text-sm font-medium text-theme-primary mb-1">Contradictions</h4>
 <p className="text-sm text-theme-secondary leading-relaxed max-w-2xl">
 Croisement des patterns pour révéler les stratégies qui se contredisent.
 </p>
 </div>
 )}
 </div>

 {/* STATS DYNAMIQUES selon le tab actif */}
 <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
 
 {/* Stats Patterns */}
 {memorySubTab === 'patterns' && (
 <>
 <div 
 className="p-4 border border-theme bg-theme-surface"
 >
 <div className="text-3xl font-light text-theme-primary mb-1">{businessPatterns.length}</div>
 <div className="text-xs text-theme-secondary">Patterns détectés</div>
 </div>
 <div 
 className="p-4 border border-theme bg-theme-surface"
 >
 <div className="text-3xl font-light text-green-600 dark:text-green-400 mb-1">
 {businessPatterns.filter(p => p.is_validated_by_user).length}
 </div>
 <div className="text-xs text-theme-secondary">Patterns validés</div>
 </div>
 <div 
 className="p-4 border border-theme bg-theme-surface"
 >
 <div className="text-3xl font-light text-purple-600 dark:text-purple-400 mb-1">
 {Math.round(businessPatterns.reduce((sum, p) => sum + p.confidence_score, 0) / businessPatterns.length * 100)}%
 </div>
 <div className="text-xs text-theme-secondary">Confiance moyenne</div>
 </div>
 <div 
 className="p-4 border border-theme bg-theme-surface"
 >
 <div className="text-3xl font-light text-blue-600 dark:text-blue-400 mb-1">
 {businessPatterns.reduce((sum, p) => sum + (p.estimated_impact_eur || 0), 0).toLocaleString()}€
 </div>
 <div className="text-xs text-theme-secondary">Impact total estimé</div>
 </div>
 </>
 )}

 {/* Stats DNA Score */}
 {memorySubTab === 'dna' && (
 <>
 <div 
 className="p-4 border border-theme bg-theme-surface"
 >
 <div className="text-3xl font-light text-theme-primary mb-1">
 {maturityScore && maturityScore.score !== null ? maturityScore.score : '—'}
 </div>
 <div className="text-xs text-theme-secondary">Score global</div>
 </div>
 <div 
 className="p-4 border border-theme bg-theme-surface"
 >
 <div className="text-3xl font-light text-purple-600 dark:text-purple-400 mb-1">
 {maturityScore && maturityScore.score !== null 
 ? maturityScore.score >= 80 ? 'Top 10%' 
 : maturityScore.score >= 60 ? 'Top 40%' 
 : maturityScore.score >= 40 ? 'Top 70%' 
 : 'Débutant'
 : '—'}
 </div>
 <div className="text-xs text-theme-secondary">Classement</div>
 </div>
 <div 
 className="p-4 border border-theme bg-theme-surface"
 >
 <div className="text-3xl font-light text-blue-600 dark:text-blue-400 mb-1">
 {maturityScore && maturityScore.breakdown ? '5' : '—'}
 </div>
 <div className="text-xs text-theme-secondary">Dimensions analysées</div>
 </div>
 <div 
 className="p-4 border border-theme bg-theme-surface"
 >
 <div className="text-3xl font-light text-green-600 dark:text-green-400 mb-1">
 {maturityScore && maturityScore.recommendations ? maturityScore.recommendations.length : '—'}
 </div>
 <div className="text-xs text-theme-secondary">Recommandations</div>
 </div>
 </>
 )}

 {/* Stats Collisions */}
 {memorySubTab === 'collisions' && (
 <>
 <div 
 className="p-4 border border-theme bg-theme-surface"
 >
 <div className="text-3xl font-light text-theme-primary mb-1">{patternCollisions.length}</div>
 <div className="text-xs text-theme-secondary">Collisions détectées</div>
 </div>
 <div 
 className="p-4 border border-theme bg-theme-surface"
 >
 <div className="text-3xl font-light text-red-600 dark:text-red-400 mb-1">
 {patternCollisions.filter(c => c.collision_type === 'contradiction').length}
 </div>
 <div className="text-xs text-theme-secondary">Contradictions</div>
 </div>
 <div 
 className="p-4 border border-theme bg-theme-surface"
 >
 <div className="text-3xl font-light text-orange-600 dark:text-purple-600 dark:text-purple-400 mb-1">
 {patternCollisions.filter(c => c.severity_score >= 0.7).length}
 </div>
 <div className="text-xs text-theme-secondary">Critiques (≥70%)</div>
 </div>
 <div 
 className="p-4 border border-theme bg-theme-surface"
 >
 <div className="text-3xl font-light text-blue-600 dark:text-blue-400 mb-1">
 {patternCollisions.reduce((sum, c) => sum + (c.combined_impact_eur || 0), 0).toLocaleString()}€
 </div>
 <div className="text-xs text-theme-secondary">Impact combiné</div>
 </div>
 </>
 )}
 </div>
 </div>

 {/* TAB 1: Patterns Détectés */}
 {memorySubTab === 'patterns' && (
 businessPatterns.length === 0 && !patternsLoading ? (
 <div className="text-center py-20">
 <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center">
 <Sparkles className="w-16 h-16 text-theme-primary" />
 </div>
 <h3 className="text-lg font-medium text-theme-primary mb-2">
 Aucun pattern détecté
 </h3>
 <p className="text-sm text-theme-primary mb-6 max-w-sm mx-auto">
 Analysez vos données avec FluidChat pour détecter des comportements récurrents
 </p>
 <button
 onClick={triggerPatternDetection}
 className="px-5 py-2 bg-theme-surface hover:bg-theme-surface/15 text-theme-primary rounded-lg transition-colors text-sm font-medium"
 >
 Lancer la détection
 </button>
 </div>
 ) : (
 <div className="space-y-6">
 
 {/* Liste des patterns - Design épuré avec filtres avancés */}
 <div className="bg-theme-surface rounded-xl border border-theme">
 <div className="px-6 py-4 border-b border-theme">
 <div className="flex items-center justify-between mb-4">
 <h3 className="text-sm font-medium text-theme-primary">
 Patterns ({getFilteredAndSortedPatterns().length} / {businessPatterns.length})
 </h3>
 
 {/* Tri */}
 <select 
 value={patternFilter.sortBy}
 onChange={(e) => setPatternFilter({...patternFilter, sortBy: e.target.value})}
 className="bg-theme-hover border border-theme rounded px-3 py-1 text-xs text-theme-primary focus:outline-none focus:border-theme"
 >
 <option value="impact">Tri par impact €</option>
 <option value="confidence">Tri par confiance</option>
 <option value="date">Tri par date</option>
 <option value="occurrences">Tri par occurrences</option>
 </select>
 </div>
 
 {/* Barre de recherche + filtres */}
 <div className="flex items-center gap-3">
 <div className="flex-1">
 <input
 type="text"
 placeholder="Rechercher un pattern..."
 value={patternFilter.search}
 onChange={(e) => {
 setPatternFilter({...patternFilter, search: e.target.value})
 setCurrentPage(1) // Reset à la page 1
 }}
 className="w-full bg-theme-hover border border-theme rounded px-3 py-2 text-sm text-theme-primary placeholder:text-theme-primary focus:outline-none focus:border-theme"
 />
 </div>
 
 <select 
 value={patternFilter.type}
 onChange={(e) => {
 setPatternFilter({...patternFilter, type: e.target.value})
 setCurrentPage(1)
 }}
 className="bg-theme-hover border border-theme rounded px-3 py-2 text-xs text-theme-primary focus:outline-none focus:border-theme"
 >
 <option value="all">Tous types</option>
 <option value="anomaly">Anomalies</option>
 <option value="correlation">Corrélations</option>
 <option value="trend">Tendances</option>
 <option value="risk">Risques</option>
 <option value="opportunity">Opportunités</option>
 <option value="seasonality">Saisonnalité</option>
 <option value="customer_behavior">Comportement client</option>
 </select>
 
 <select 
 value={patternFilter.status}
 onChange={(e) => {
 setPatternFilter({...patternFilter, status: e.target.value})
 setCurrentPage(1)
 }}
 className="bg-theme-hover border border-theme rounded px-3 py-2 text-xs text-theme-primary focus:outline-none focus:border-theme"
 >
 <option value="all">Tous statuts</option>
 <option value="validated">Validés</option>
 <option value="pending">À valider</option>
 </select>
 
 {/* Reset filtres */}
 {(patternFilter.type !== 'all' || patternFilter.status !== 'all' || patternFilter.search) && (
 <button
 onClick={() => {
 setPatternFilter({type: 'all', status: 'all', search: '', sortBy: 'impact'})
 setCurrentPage(1)
 }}
 className="px-3 py-2 bg-theme-hover hover:bg-theme-surface text-xs text-theme-primary rounded transition-colors"
 >
 Réinitialiser
 </button>
 )}
 </div>
 </div>
 
 <div className="p-6">
 {getPaginatedPatterns().length === 0 ? (
 <div className="text-center py-12">
 <div className="text-sm text-theme-primary mb-2">Aucun pattern trouvé</div>
 <div className="text-xs text-theme-primary">
 Essayez de modifier vos filtres ou votre recherche
 </div>
 </div>
 ) : (
 <>
 <div className="space-y-3 mb-6">
 {getPaginatedPatterns().map((pattern) => (
 <BusinessPattern
 key={pattern.id}
 pattern={pattern}
 onValidate={validatePattern}
 onDelete={deletePattern}
 onCreateNotionAction={createNotionAction}
 />
 ))}
 </div>
 
 {/* Pagination */}
 {totalPages > 1 && (
 <div className="flex items-center justify-between pt-4 border-t border-theme">
 <div className="text-xs text-theme-primary">
 Page {currentPage} sur {totalPages} • {patternsPerPage} par page
 </div>
 <div className="flex items-center gap-2">
 <button
 onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
 disabled={currentPage === 1}
 className="px-3 py-1 bg-theme-hover hover:bg-theme-surface disabled:opacity-30 disabled:cursor-not-allowed text-xs text-theme-primary rounded transition-colors"
 >
 Précédent
 </button>
 
 {/* Pages */}
 <div className="flex items-center gap-1">
 {Array.from({length: Math.min(5, totalPages)}, (_, i) => {
 let pageNum;
 if (totalPages <= 5) {
 pageNum = i + 1;
 } else if (currentPage <= 3) {
 pageNum = i + 1;
 } else if (currentPage >= totalPages - 2) {
 pageNum = totalPages - 4 + i;
 } else {
 pageNum = currentPage - 2 + i;
 }
 
 return (
 <button
 key={pageNum}
 onClick={() => setCurrentPage(pageNum)}
 className={`w-8 h-8 rounded text-xs font-medium transition-colors ${
 currentPage === pageNum
 ? 'bg-theme-surface/20 text-theme-primary'
 : 'bg-theme-hover text-theme-primary hover:bg-theme-surface'
 }`}
 >
 {pageNum}
 </button>
 );
 })}
 </div>
 
 <button
 onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
 disabled={currentPage === totalPages}
 className="px-3 py-1 bg-theme-hover hover:bg-theme-surface disabled:opacity-30 disabled:cursor-not-allowed text-xs text-theme-primary rounded transition-colors"
 >
 Suivant
 </button>
 </div>
 </div>
 )}
 </>
 )}
 </div>
 </div>
 </div>
 ))}

 {/* TAB 2: DNA Score */}
 {memorySubTab === 'dna' && (
 <div className="max-w-4xl mx-auto">
 <div className="mb-12">
 <p className="page-kicker mb-3">Maturité</p>
 <h2 className="font-display text-2xl font-medium text-theme-primary mb-3">DNA Score</h2>
 <p className="text-sm text-theme-secondary max-w-2xl leading-relaxed">
 Capacité à transformer vos données en avantage. Cinq dimensions, un chiffre.
 </p>
 </div>

 {scoreLoading ? (
 <div className="flex items-center justify-center py-20">
 <div className="w-8 h-8 border border-theme-text-primary border-t-transparent animate-spin" />
 </div>
 ) : maturityScore && maturityScore.score !== null ? (
 <div className="space-y-8">
 <div className="border border-theme bg-theme-surface p-12 text-center">
 <div className="font-display text-8xl font-medium mb-4 tabular-nums" style={{ color: 'var(--spark-gold)' }}>
 {maturityScore.score}
 </div>
 <div className="text-lg font-display mb-2">
 {maturityScore.score >= 80 ? (
 <span>Excellence · Top 10%</span>
 ) : maturityScore.score >= 60 ? (
 <span>En progression · Top 40%</span>
 ) : maturityScore.score >= 40 ? (
 <span>Démarrage · Top 70%</span>
 ) : (
 <span>Potentiel à développer</span>
 )}
 </div>
 <div className="text-sm text-theme-tertiary">
 Calculé le {new Date(maturityScore.calculatedAt || Date.now()).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
 </div>
 </div>

 {maturityScore.breakdown && (
 <div className="grid grid-cols-5 gap-px bg-theme-border border border-theme">
 {Object.entries(maturityScore.breakdown).map(([key, data]: [string, any]) => (
 <div key={key} className="bg-theme-surface p-5 text-center">
 <div className="font-display text-3xl text-theme-primary mb-3 tabular-nums">{data.score}</div>
 <div className="text-xs font-medium text-theme-secondary mb-3">{data.label}</div>
 <div className="w-full h-px bg-theme-hover overflow-hidden mb-3">
 <div 
 className="h-full"
 style={{ width: `${data.score}%`, background: 'var(--spark-gold)' }}
 />
 </div>
 <div className="text-xs text-theme-tertiary leading-relaxed">{data.details}</div>
 </div>
 ))}
 </div>
 )}

 {maturityScore.recommendations && maturityScore.recommendations.length > 0 && (
 <div className="border border-theme bg-theme-surface p-6">
 <h3 className="font-display text-lg mb-4">
 Comment atteindre {Math.min(100, maturityScore.score + 20)}/100
 </h3>
 <div className="space-y-3">
 {maturityScore.recommendations.map((rec: any, idx: number) => (
 <div key={idx} className="flex items-start gap-4 p-4 border border-theme">
 <div className="w-7 h-7 border border-theme flex items-center justify-center flex-shrink-0 font-mono text-xs">
 {idx + 1}
 </div>
 <div className="flex-1">
 <div className="text-sm font-medium text-theme-primary mb-1">{rec.title}</div>
 <div className="text-xs text-theme-secondary mb-2">{rec.description}</div>
 <div className="page-kicker">Impact: {rec.impact}</div>
 </div>
 </div>
 ))}
 </div>
 </div>
 )}

 <div className="flex items-center justify-center gap-3">
 <button
 onClick={calculateMaturityScore}
 className="btn-modern btn-secondary"
 >
 Recalculer
 </button>
 <button 
 onClick={() => {
 const url = encodeURIComponent(window.location.origin);
 const text = encodeURIComponent(`Mon Business DNA Score Clairify: ${maturityScore.score}/100 `);
 window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${url}&summary=${text}`, '_blank', 'width=600,height=600,noopener');
 }}
 className="btn-modern btn-primary"
 >
 Partager
 </button>
 </div>
 </div>
 ) : (
 <div className="text-center py-20 border border-theme bg-theme-surface">
 <h3 className="font-display text-xl mb-3">
 Score non calculé
 </h3>
 <p className="text-sm text-theme-secondary mb-8 max-w-md mx-auto">
 Un chiffre pour situer votre maturité, et des priorités pour progresser.
 </p>
 <button
 onClick={calculateMaturityScore}
 disabled={scoreLoading}
 className="btn-modern btn-primary"
 >
 Calculer le DNA Score
 </button>
 </div>
 )}
 </div>
 )}

 {/* TAB 3: Collisions */}
 {memorySubTab === 'collisions' && (
 <div className="max-w-4xl mx-auto">
 {/* Hero explanation */}
 <div className="mb-12">
 <p className="page-kicker mb-3">Tensions</p>
 <h2 className="font-display text-2xl font-medium mb-3">Collisions</h2>
 <p className="text-sm text-theme-secondary max-w-2xl leading-relaxed">
 Contradictions entre patterns. Les décisions qui se neutralisent, et l'argent qu'elles coûtent.
 </p>
 </div>
 
                <div className="flex gap-6 mb-8 border-b border-theme">
                  <button
                    onClick={() => setCollisionsFilter('active')}
                    className={`studio-tab ${
                      collisionsFilter === 'active' ? 'is-active' : ''
                    }`}
                  >
                    Actives ({patternCollisions.filter(c => !c.is_resolved).length})
                  </button>
                  <button
                    onClick={() => setCollisionsFilter('resolved')}
                    className={`studio-tab ${
                      collisionsFilter === 'resolved' ? 'is-active' : ''
                    }`}
                  >
                    Résolues ({patternCollisions.filter(c => c.is_resolved).length})
                  </button>
                  <button
                    onClick={() => setCollisionsFilter('all')}
                    className={`studio-tab ${
                      collisionsFilter === 'all' ? 'is-active' : ''
                    }`}
                  >
                    Toutes ({patternCollisions.length})
                  </button>
                </div>

 {collisionsLoading ? (
 <div className="flex items-center justify-center py-20">
 <div className="w-12 h-12 border-4 border-theme border-t-red-500 rounded-full animate-spin" />
 </div>
 ) : getFilteredCollisions().length > 0 ? (
 <div className="space-y-4">
 {getFilteredCollisions().map((collision) => (
<div key={collision.id} className={`rounded-lg border p-6 transition-all ${
collision.is_resolved 
? 'bg-green-50 border-green-300 dark:bg-green-900/10 dark:border-green-600/30' 
: 'bg-red-50 border-red-300 dark:bg-gray-800/50 dark:border-gray-700'
}`}>
 <div className="flex items-start justify-between mb-4">
 <div className="flex items-center gap-3">
<span className={`px-3 py-1 rounded-full text-xs font-medium uppercase ${
collision.is_resolved
? 'bg-green-200 text-green-800 dark:bg-green-900/30 dark:text-green-300'
: 'bg-red-200 text-red-800 dark:bg-gray-700 dark:text-red-300'
}`}>
 {collision.is_resolved ? 'Résolu' : (
 collision.collision_type === 'contradiction' ? 'Contradiction' : 
 collision.collision_type === 'opportunity' ? 'Synergie' : 
 'Risque Amplifié'
 )}
 </span>
 {!collision.is_resolved && (
 <div className="flex items-center gap-1">
 {[...Array(5)].map((_, i) => (
 <div 
 key={i} 
 className={`w-2 h-4 rounded-sm ${
 i < Math.ceil(collision.severity_score * 5) 
 ? 'bg-red-500 dark:bg-red-500' 
 : 'bg-gray-200 dark:bg-gray-700'
 }`} 
 />
 ))}
 </div>
 )}
 </div>
 <div className="text-right">
 <div className={`text-3xl font-light ${
 collision.is_resolved 
 ? 'text-green-600 dark:text-green-400' 
 : 'text-red-600 dark:text-red-400'
 }`}>
 {collision.combined_impact_eur ? `${collision.combined_impact_eur.toLocaleString()}€` : 'N/A'}
 </div>
 <div className={`text-xs ${
 collision.is_resolved 
 ? 'text-green-600 dark:text-green-300' 
 : 'text-red-600 dark:text-red-300'
 }`}>Impact combiné</div>
 </div>
 </div>
 
 <p className="text-sm text-theme-primary leading-relaxed mb-4">
 {collision.explanation}
 </p>
 
 <div className="flex gap-2 flex-wrap mb-4">
 <div className="px-4 py-2 bg-theme-elevated border border-blue-400 dark:border-blue-600/40 text-blue-700 dark:text-blue-300 rounded-lg text-sm font-medium">
 {collision.pattern_1?.title || 'Pattern 1'}
 </div>
 <div className={`font-bold self-center ${
 collision.is_resolved 
 ? 'text-green-600 dark:text-green-400' 
 : 'text-red-600 dark:text-red-400'
 }`}>VS</div>
 <div className="px-4 py-2 bg-theme-elevated border border-purple-400 dark:border-purple-600/40 text-purple-700 dark:text-purple-300 rounded-lg text-sm font-medium">
 {collision.pattern_2?.title || 'Pattern 2'}
 </div>
 </div>
 
 {collision.recommended_actions && collision.recommended_actions.length > 0 && (
 <div className="pt-4 border-t border-gray-300 dark:border-gray-700">
 <div className="text-xs font-medium text-red-700 dark:text-red-200 mb-3 flex items-center gap-2">
 <Zap className="w-4 h-4" />
 Actions recommandées:
 </div>
 <div className="space-y-2">
 {collision.recommended_actions.map((action: string, idx: number) => (
 <div key={idx} className="flex items-start gap-3 text-sm text-theme-primary">
<div className="w-6 h-6 rounded-full bg-red-200 dark:bg-gray-700 flex items-center justify-center flex-shrink-0 text-red-700 dark:text-red-400 text-xs font-medium">
{idx + 1}
</div>
 <span className="flex-1">{action}</span>
 </div>
 ))}
 </div>
 </div>
 )}
 
 {/* Boutons d'action - Style BusinessPattern */}
 <div className="mt-4 flex items-center justify-between pt-3 border-t border-theme-divider">
 <div className="text-xs text-theme-secondary">
 {collision.is_resolved ? 'Résolu' : 'Actions disponibles'}
 </div>
 
 <div className="flex items-center space-x-2">
 {/* Bouton Notion - Même style que Business Patterns */}
 {notionIntegration && (
 collision.notion_page_id ? (
 <a
 href={collision.notion_url}
 target="_blank"
 rel="noopener noreferrer"
 className="flex items-center space-x-1 text-xs text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/20 px-2 py-1 rounded-lg border border-purple-200/50 dark:border-purple-500/30 hover:bg-purple-100 dark:hover:bg-purple-900/30 transition-colors"
 >
 <CheckCircle className="w-3 h-3" />
 <span>Dans Notion</span>
 <ExternalLink className="w-3 h-3" />
 </a>
 ) : (
 <button
 onClick={(e) => {
 e.stopPropagation();
 syncCollisionToNotion(collision.id);
 }}
 disabled={syncingNotion[collision.id]}
 className="px-3 py-1.5 bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 disabled:from-gray-400 disabled:to-gray-500 text-white text-xs font-medium rounded-lg transition-all flex items-center space-x-1 shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
 title="Synchroniser avec Notion"
 >
 {syncingNotion[collision.id] ? (
 <>
 <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
 <span>Sync...</span>
 </>
 ) : (
 <>
 <Plus className="w-3 h-3" />
 <span>Notion</span>
 </>
 )}
 </button>
 )
 )}
 
 {/* Bouton Résoudre */}
 {!collision.is_resolved && (
 <button
 onClick={(e) => {
 e.stopPropagation();
 resolveCollision(collision.id);
 }}
 className="px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white text-xs font-medium rounded-lg transition-colors flex items-center space-x-1 shadow-sm hover:shadow-md"
 title="Marquer comme résolu"
 >
 <CheckCircle className="w-3 h-3" />
 <span>Résoudre</span>
 </button>
 )}
 </div>
 </div>
 </div>
 ))}
 </div>
 ) : (
 <div className="text-center py-20">
 <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-green-500/10 to-emerald-500/10 rounded-full mb-6">
 <CheckCircle className="w-10 h-10 text-green-400/50" />
 </div>
 <h3 className="text-xl font-light text-theme-primary mb-3">
 Aucune collision détectée
 </h3>
 <p className="text-sm text-theme-primary mb-8 max-w-md mx-auto">
 Vos patterns business sont cohérents. Lancez une détection pour analyser vos données et identifier d'éventuelles contradictions.
 </p>
 <button
 onClick={detectCollisions}
 disabled={collisionsLoading}
 className="px-8 py-3 bg-gradient-to-r from-red-600 to-indigo-600 dark:to-indigo-500 hover:from-red-700 hover:to-orange-700 text-theme-primary rounded-lg font-medium transition-all inline-flex items-center gap-2"
 >
 <Search className="w-5 h-5" />
 <span>Détecter les collisions</span>
 </button>
 </div>
 )}
 </div>
 )}
 </div>
 )
    case 'big-data':
      return <BigDataAnalytics />
    default:
      return null
 }
 }

 const sidebarSections = [
 { id: 'files', label: 'Fichiers', icon: Folder },
 { id: 'workbench', label: 'Sources', icon: Database },
 { id: 'knowledge', label: 'Savoir', icon: BookOpen },
 { id: 'history', label: 'Sessions', icon: Clock },
 ] as const

 const renderSidebarContent = () => {
 switch (sidebarSection) {
 case 'files':
 return (
 <div className="p-4">
 <div className="mb-4">
 <Button
 onClick={() => document.getElementById('file-upload')?.click()}
 className="w-full"
 >
 <Upload size={16} className="mr-2" />
 Importer
 </Button>
 <input
 id="file-upload"
 type="file"
 accept=".csv,.xlsx,.xls"
 onChange={handleFileUpload}
 className="hidden"
 />
 
 {/* Option d'analyse directe */}
 <div className="mt-3 p-3 border border-theme bg-theme-hover">
 <div className="flex items-center space-x-2 mb-2">
 <span className="page-kicker">Analyse directe</span>
 </div>
 <p className="text-xs text-theme-secondary mb-2">
 Importez un fichier et lancez l'analyse dans la console.
 </p>
 <Button
 variant="outline"
 onClick={() => {
 const input = document.createElement('input');
 input.type = 'file';
 input.accept = '.csv,.xlsx,.xls';
 input.onchange = async (e: any) => {
 const file = e.target.files?.[0];
 if (!file) return;
 
 
 // Traiter le fichier et l'envoyer directement à l'IA
 try {
 const text = await file.text();
 
 // Créer ou récupérer une session de chat
 const sessionTitle = `Analyse de ${file.name}`;
 let sessionId = activeSessionId;
 
 if (!sessionId) {
 // Créer une nouvelle session
 // SÉCURITÉ: Utiliser authFetch pour JWT
 const response = await authFetch(`${API_URL}/api/chat/create-session`, {
 method: 'POST',
 body: JSON.stringify({ title: sessionTitle })
 });
 if (response.ok) {
 const data = await response.json();
 sessionId = data.sessionId;
 setActiveSessionId(sessionId);
 }
 }
 
 // Passer à l'onglet console IA et envoyer les données
 setTerminalOpen(true);
 
 // Simuler l'envoi du fichier à l'IA
 const analysisPrompt = ` **Fichier analysé:** ${file.name}\n\n**Données CSV:**\n\`\`\`csv\n${text.substring(0, 2000)}${text.length > 2000 ? '...\n[Données tronquées pour l\'aperçu]' : ''}\n\`\`\`\n\n**Demande d'analyse:**\nAnalyse ces données en détail et génère des insights actionnables avec des recommandations stratégiques.`;
 
 // SÉCURITÉ: Notification avec DOM API (pas innerHTML)
 const notification = document.createElement('div');
 notification.className = 'fixed top-4 right-4 z-50 bg-gradient-to-r from-blue-600 to-purple-600 text-theme-primary px-6 py-3 rounded-lg shadow-xl';
 
 const topRow = document.createElement('div');
 topRow.className = 'flex items-center space-x-2';
 
 const iconWrapper = document.createElement('div');
 iconWrapper.className = 'w-5 h-5 bg-theme-surface rounded-full flex items-center justify-center';
 const iconInner = document.createElement('div');
 iconInner.className = 'w-2 h-2 bg-blue-600 rounded-full animate-pulse';
 iconWrapper.appendChild(iconInner);
 
 const mainText = document.createElement('span');
 mainText.className = 'font-bold';
 mainText.textContent = ' Fichier envoyé à l\'IA!';
 
 topRow.appendChild(iconWrapper);
 topRow.appendChild(mainText);
 
 const subText = document.createElement('div');
 subText.className = 'text-xs mt-1 opacity-90';
 subText.textContent = 'Ouvrez la Console IA pour voir l\'analyse';
 
 notification.appendChild(topRow);
 notification.appendChild(subText);
 document.body.appendChild(notification);
 setTimeout(() => notification.remove(), 5000);
 
 } catch (error) {
 logger.error('Erreur upload IA:', error);
 alert('Erreur lors de l\'upload du fichier');
 }
 };
 input.click();
 }}
 size="sm"
 className="w-full"
 >
 <Brain size={14} className="mr-2" />
 Analyser
 </Button>
 </div>
 </div>
 <div className="space-y-2 max-h-96 overflow-y-auto">
 {files.length === 0 ? (
 <div className="text-center py-8 text-theme-secondary">
 <CreationSpark variant="mark" className="w-10 h-10 mx-auto mb-3 opacity-40" />
 <div className="text-sm">Aucun fichier</div>
 <div className="text-xs mt-1 text-theme-tertiary">CSV, Excel</div>
 </div>
 ) : (
 files.map((file) => (
 <div
 key={file.id}
 className="p-3 rounded-lg bg-theme-hover hover:bg-theme-surface transition-colors cursor-pointer"
 >
 <div className="flex items-center space-x-2">
 <FileText size={14} className="text-green-400" />
 <span className="text-sm text-theme-primary truncate">{file.file_name}</span>
 </div>
 <div className="text-xs text-theme-secondary mt-1">
 {file.rows_count} lignes • {new Date(file.created_at).toLocaleDateString('fr-FR')}
 </div>
 </div>
 ))
 )}
 </div>
 </div>
 )
 case 'workbench':
 return (
 <div className="p-4 space-y-4">
 {/* Notion Integration */}
 <div className="card-modern bg-accent-violet-light p-4">
 {/* Header avec logo et bouton */}
 <div className="flex items-start justify-between gap-2 mb-3">
 <div className="flex items-center gap-2 flex-1 min-w-0">
 <div className="w-8 h-8 bg-theme-surface rounded-lg flex items-center justify-center flex-shrink-0">
 <svg className="w-5 h-5 text-theme-primary" viewBox="0 0 100 100" fill="currentColor">
 <path d="M6.017 4.313l55.333 -4.087c6.797 -0.583 8.543 -0.19 12.817 2.917l17.663 12.443c2.913 2.14 3.883 2.723 3.883 5.053v68.243c0 4.277 -1.553 6.807 -6.99 7.193L24.467 99.967c-4.08 0.193 -6.023 -0.39 -8.16 -3.113L3.3 79.94c-2.333 -3.113 -3.3 -5.443 -3.3 -8.167V11.113c0 -3.497 1.553 -6.413 6.017 -6.8z"/>
 </svg>
 </div>
 <div className="flex-1 min-w-0">
 <div className="text-xs font-semibold text-theme-primary truncate">Notion</div>
 </div>
 </div>
 
 {/* Bouton Connecter/Déconnecter */}
 {notionIntegration ? (
 <button
 onClick={disconnectNotion}
 className="px-2.5 py-1 bg-error hover:bg-error/80 text-error-text rounded-md text-[10px] transition-colors font-medium flex-shrink-0"
 >
 Déconnecter
 </button>
 ) : (
 <button
 onClick={connectNotion}
 className="px-2.5 py-1 bg-accent-violet hover:bg-accent-violet-hover text-theme-inverse rounded-md text-[10px] transition-colors font-medium flex-shrink-0"
 >
 Connecter
 </button>
 )}
 </div>

 {/* Informations détaillées */}
 {notionIntegration && (
 <div className="space-y-2">
 {/* Workspace et statut */}
 <div className="flex items-center gap-2">
 <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse flex-shrink-0" />
 <div className="text-xs text-theme-primary font-medium truncate">
 {notionIntegration.workspace_name}
 </div>
 </div>

 {/* Statistiques */}
 <div className="grid grid-cols-2 gap-2 pt-2 border-t border-theme">
 <div>
 <div className="text-[10px] text-theme-primary">Actions</div>
 <div className="text-sm font-semibold text-theme-primary">
 {businessPatterns.filter(p => p.notion_action_id).length}
 </div>
 </div>
 <div>
 <div className="text-[10px] text-theme-primary">Connecté</div>
 <div className="text-xs font-medium text-theme-primary">
 {new Date(notionIntegration.created_at || Date.now()).toLocaleDateString('fr-FR', { 
 day: '2-digit', 
 month: '2-digit' 
 })}
 </div>
 </div>
 </div>
 </div>
 )}
 
 {/* Message si non connecté */}
 {!notionIntegration && (
 <div className="flex items-center gap-2">
 <div className="w-2 h-2 bg-gray-500 rounded-full flex-shrink-0" />
 <span className="text-xs text-theme-primary">Non connecté</span>
 </div>
 )}
 </div>

 {/* Autres sources (bientôt) */}
 <div>
 <h3 className="text-sm font-medium text-theme-primary mb-3">Autres sources (bientôt)</h3>
 <div className="space-y-2">
 <div className="flex items-center gap-2 p-2 rounded-lg bg-theme-hover border border-theme opacity-50">
 <Database size={16} className="text-theme-secondary" />
 <span className="text-sm text-theme-primary">Google Sheets</span>
 </div>
 <div className="flex items-center gap-2 p-2 rounded-lg bg-theme-hover border border-theme opacity-50">
 <Database size={16} className="text-theme-secondary" />
 <span className="text-sm text-theme-primary">Airtable</span>
 </div>
 <div className="flex items-center gap-2 p-2 rounded-lg bg-theme-hover border border-theme opacity-50">
 <Database size={16} className="text-theme-secondary" />
 <span className="text-sm text-theme-primary">Slack</span>
 </div>
 </div>
 </div>
 </div>
 )
 case 'knowledge':
 return (
 <div className="p-4">
 <h3 className="text-sm font-medium text-theme-primary mb-3">Base de connaissances</h3>
 <div className="text-center text-theme-secondary py-8">
 <BookOpen size={32} className="mx-auto mb-2" />
 <p className="text-sm">Ajoutez vos documents métier</p>
 </div>
 </div>
 )
 case 'history':
 return (
 <div className="p-4 h-full">
 <h3 className="text-sm font-medium text-theme-primary mb-3">Historique des Sessions</h3>
 <div className="space-y-2 h-full overflow-y-auto scrollbar-hide">
 {(() => {
 // FILTRE: Exclure les sessions vides (0 insights ET créées il y a < 5 minutes)
 // Ceci évite d'afficher les sessions qui viennent d'être créées mais pas encore utilisées
 const now = Date.now();
 const validSessions = chatSessions.filter(s => {
 // Toujours garder les sessions avec insights
 if (s.insights_count > 0) return true;
 
 // Garder les sessions récentes (< 5 min) même sans insights (en cours d'utilisation)
 const sessionAge = now - new Date(s.created_at).getTime();
 if (sessionAge < 5 * 60 * 1000) return true;
 
 // Garder les sessions avec activité récente
 if (s.last_activity) {
 const activityAge = now - new Date(s.last_activity).getTime();
 if (activityAge < 10 * 60 * 1000) return true; // Moins de 10 min
 }
 
 // Filtrer les sessions anciennes sans insights
 return false;
 });
 
 if (validSessions.length === 0) {
 return (
 <div className="text-center py-8 text-theme-secondary">
 <div className="text-sm">Aucune session trouvée</div>
 <div className="text-xs mt-1">Vos conversations apparaîtront ici</div>
 </div>
 );
 }
 
 return (
 <>
 <div className="text-xs text-theme-secondary mb-3">
 Sessions actives: {validSessions.length}
 {chatSessions.length > validSessions.length && (
 <span className="text-theme-primary ml-1 opacity-60">
 ({chatSessions.length - validSessions.length} masquées)
 </span>
 )}
 </div>
 {validSessions.map((session) => (
 <div
 key={session.id}
 className={`group p-3 transition-colors border ${
 activeSessionId === session.id
 ? 'bg-theme-hover border-theme-text-primary'
 : 'bg-transparent hover:bg-theme-hover border-theme'
 }`}
 >
 <div className="flex items-start justify-between">
 <div 
 className="flex-1 min-w-0 cursor-pointer"
 onClick={async () => {
 if (editingSessionId === session.id) return; // Ne pas cliquer si en mode édition
 setMobileSidebarOpen(false);
 try {
 const result = await restoreSessionComplete(session.id);
 
 if (result && result.insights && result.insights.length > 0) {
 switchToTab('insights');
 } else if (result && result.reports && result.reports.length > 0) {
 switchToTab('deep-report');
 }
 } catch (error) {
 logger.error('[Session] Erreur restauration:', error);
 }
 }}
 >
 <div className="flex items-center space-x-2 mb-2">
 <div className="w-1.5 h-1.5 rounded-full bg-spark-gold" style={{ background: 'var(--spark-gold)' }}></div>
 <div className={`text-sm font-medium flex-1 ${
 activeSessionId === session.id ? 'animate-shimmer-light' : ''
 }`}>
 {/* MODE ÉDITION INLINE */}
                {editingSessionId === session.id ? (
                <input
                  type="text"
                  value={editingTitle}
                  onChange={(e) => setEditingTitle(e.target.value)}
                  onBlur={() => {
                    if (editingTitle.trim() && editingTitle !== session.title) {
                      handleRenameSession(session.id, editingTitle);
                    } else {
                      setEditingSessionId(null);
                      setEditingTitle('');
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.currentTarget.blur();
                    } else if (e.key === 'Escape') {
                      setEditingSessionId(null);
                      setEditingTitle('');
                    }
                  }}
                  onClick={(e) => e.stopPropagation()}
                  autoFocus
                  maxLength={80}
                  className="w-full px-2 py-1 bg-white dark:bg-gray-800 border border-accent-primary rounded text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-accent-primary/50 transition-colors"
                  placeholder="Titre de la session..."
                />
                ) : (
                  <span className="truncate block text-theme-primary">
                    {session.title || session.name || 'Session'}
                  </span>
                )}
 </div>
 </div>
 
 <div className="flex items-center gap-3">
 {/* COMPTEURS AMÉLIORÉS AVEC BADGES */}
 <div className="flex items-center gap-1.5">
 <div className="w-1.5 h-1.5 bg-accent-tertiary rounded-full animate-pulse"></div>
 <Brain className="w-3 h-3 text-accent-tertiary" />
 <span className="text-xs font-semibold text-accent-tertiary">{session.insights_count || 0}</span>
 </div>
 
 <div className="flex items-center gap-1.5">
 <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></div>
 <FileText className="w-3 h-3 text-green-400" />
 <span className="text-xs font-semibold text-green-400">{session.reports_count || (session.has_deep_report ? 1 : 0)}</span>
 </div>
 
 <div className="flex items-center gap-1.5">
 <div className="w-1.5 h-1.5 bg-purple-500 rounded-full animate-pulse"></div>
 <BarChart3 className="w-3 h-3 text-purple-400" />
 <span className="text-xs font-semibold text-purple-400">{session.data_viz_count || session.charts_count || 0}</span>
 </div>
 
 {/* INDICATEUR D'ACTIVITÉ RÉCENTE */}
 {session.last_activity && new Date(session.last_activity) > new Date(Date.now() - 3600000) && (
 <div className="flex items-center gap-1 text-xs text-green-400">
 <div className="w-1 h-1 bg-green-500 rounded-full animate-pulse"></div>
 <span className="opacity-75">récent</span>
 </div>
 )}
 </div>
 </div>
 
 {/* ACTIONS INLINE - MODERNE ET PROPRE */}
 <div className="flex gap-1 ml-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
 <button
 onClick={(e) => {
 e.stopPropagation();
 setEditingSessionId(session.id);
 setEditingTitle(session.title || session.name || 'Session');
 }}
 className="p-1.5 rounded-lg bg-theme-surface/50 hover:bg-accent-primary/20 hover:text-accent-primary text-theme-secondary transition-all duration-200 hover:scale-110"
 title="Renommer"
 >
 <Edit2 className="w-3.5 h-3.5" />
 </button>
 <button
 onClick={(e) => {
 e.stopPropagation();
 handleDeleteSession(session.id, session.title || session.name || 'Session');
 }}
 className="p-1.5 rounded-lg bg-theme-surface/50 hover:bg-red-500/20 hover:text-red-500 text-theme-secondary transition-all duration-200 hover:scale-110"
 title="Supprimer"
 >
 <Trash2 className="w-3.5 h-3.5" />
 </button>
 </div>
 </div>
 </div>
 ))}
 </>
 );
 })()}
 
 {chatSessions.length > 0 && (
 <div className="mt-4 p-3 bg-theme-hover rounded-xl border border-theme">
 <div className="text-xs text-theme-primary space-y-1">
 <div className="flex justify-between">
 <span>Analyses totales:</span>
 <span className="text-theme-primary">{chatSessions.length}</span>
 </div>
 <div className="flex justify-between">
 <span>Sessions avec insights:</span>
 <span className="text-theme-primary">{chatSessions.filter(s => s.insights_count > 0).length}</span>
 </div>
 <div className="flex justify-between">
 <span>Insights générés:</span>
 <span className="text-theme-primary">{chatSessions.reduce((acc, s) => acc + (s.insights_count || 0), 0)}</span>
 </div>
 <div className="flex justify-between">
 <span>Rapports créés:</span>
 <span className="text-theme-primary">{chatSessions.filter(s => s.has_deep_report).length}</span>
 </div>
 </div>
 </div>
 )}
 </div>
 </div>
 )
 }
 }

 return (
 <div className="h-screen w-screen studio-shell text-theme-primary overflow-hidden fixed inset-0">
 <div className="flex h-full w-full relative z-10">
 {/* Rail */}
 <div className="studio-rail flex flex-col items-center py-5 flex-shrink-0">
 <button
 onClick={() => setTerminalOpen(true)}
 className="mb-8"
 title="Console"
 aria-label="Ouvrir la console"
 >
 <CreationSpark variant="mark" className="w-8 h-8" />
 </button>

 <div className="flex flex-col gap-1">
 {sidebarSections.map((section) => (
 <button
 key={section.id}
 onClick={() => setSidebarSection(section.id)}
 className={`studio-rail-btn group relative ${sidebarSection === section.id ? 'is-active' : ''}`}
 title={section.label}
 >
 <section.icon size={16} strokeWidth={1.5} />
 <span className="absolute left-12 px-2 py-1 bg-theme-surface border border-theme text-[11px] tracking-wide opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-50">
 {section.label}
 </span>
 </button>
 ))}
 </div>

 <div className="mt-auto">
 {profile && (
 <div className="text-[10px] tracking-widest text-theme-tertiary writing-vertical" style={{ writingMode: 'vertical-rl' }}>
 {profile.credits_remaining}
 </div>
 )}
 </div>
 </div>

 {sidebarSection !== 'files' && (
 <div className="w-72 studio-panel flex flex-col">
 <div className="px-5 py-4 border-b border-theme">
 <p className="page-kicker">{sidebarSections.find(s => s.id === sidebarSection)?.label}</p>
 </div>
 <div className="flex-1 overflow-y-auto scrollbar-hide">
 {renderSidebarContent()}
 </div>
 </div>
 )}

 <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
 <div className="studio-topbar flex items-center justify-between px-4 lg:px-8 flex-shrink-0">
 <div className="flex items-center min-w-0">
 <button
 onClick={() => setMobileSidebarOpen(!mobileSidebarOpen)}
 className="lg:hidden mr-3 p-2 text-theme-primary"
 aria-label="Toggle menu"
 >
 <Menu size={18} strokeWidth={1.5} />
 </button>

 <Link to="/" className="flex items-center mr-8 shrink-0">
 <Logo wordmark />
 </Link>

 <nav className="hidden md:flex items-end h-full overflow-x-auto scrollbar-hide pt-3">
 {tabs.map((tab) => (
 <button
 key={tab.id}
 onClick={() => {
 switchToTab(tab.id as any);
 if (tab.id === 'deep-report') {
 setHasNewDeepReport(false);
 }
 }}
 className={`studio-tab ${currentTab === tab.id ? 'is-active' : ''}`}
 >
 {tab.label}
 {tab.id === 'deep-report' && hasNewDeepReport && currentTab !== 'deep-report' && (
 <span className="ml-2 inline-block w-1.5 h-1.5 rounded-full" style={{ background: 'var(--spark-gold)' }} />
 )}
 </button>
 ))}
 </nav>
 </div>

 <div className="flex items-center gap-1 relative z-[100]">
 <ThemeToggle />
 <div className="hidden sm:block">
 <TokenCounter />
 </div>
 <UserMenu />
 </div>
 </div>

 <div className="flex-1 flex flex-col overflow-hidden relative">
 <div className="pointer-events-none absolute inset-0 opacity-[0.16]">
 <CreationSpark variant="watermark" className="w-full h-full" />
 </div>
 <div className="relative z-10 flex-1 p-5 lg:p-8 overflow-y-auto min-h-0">
 {currentAnalysis && currentAnalysis.status === 'processing' ? (
 <AnalysisProgress 
 status="running"
 progress={65}
 message="Analyse des patterns et corrélations..."
 />
 ) : (
 renderTabContent()
 )}
 </div>
 </div>
 </div>

 <div className={`fixed inset-0 z-50 bg-theme-base overflow-hidden ${terminalOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}>
 <div className="studio-topbar flex items-center justify-between px-4 lg:px-8">
 <div className="flex items-center gap-4">
 <CreationSpark variant="mark" className="w-7 h-7" />
 <div>
 <p className="page-kicker">Console</p>
 <p className="font-display text-lg leading-none mt-0.5">Dialogue</p>
 </div>
 </div>
 <Button
 variant="ghost"
 size="sm"
 onClick={() => setTerminalOpen(false)}
 title="Minimiser"
 >
 <ChevronDown size={18} strokeWidth={1.5} />
 </Button>
 </div>
 <div className="overflow-hidden" style={{ height: 'calc(100vh - 56px)' }}>
 <FluidChat 
 onAnalysisComplete={handleInsightsGenerated}
 sessionId={activeSessionId || undefined}
 onSessionCreated={(sessionId) => {
 setActiveSessionId(sessionId);
 loadChatSessions();
 }}
 className="h-full"
 />
 </div>
 </div>

 {!terminalOpen && (
 <button
 onClick={() => setTerminalOpen(true)}
 className="studio-console-fab"
 >
 <CreationSpark variant="mark" className="w-5 h-5" />
 <span className="hidden sm:inline">Console</span>
 </button>
 )}
 </div>

 {/* PDF Viewer Modal */}
 <PDFViewer
 isOpen={pdfViewerOpen}
 pdfUrl={currentPdfUrl || null}
 onClose={() => setPdfViewerOpen(false)}
 title="Rapport d'analyse généré"
 />

 {/* Modal Tokens Insuffisants Deep Report */}
 <InsufficientTokensDeepReportModal
 isOpen={showInsufficientTokensModal}
 onClose={() => setShowInsufficientTokensModal(false)}
 currentBalance={balance}
 requiredTokens={requiredTokensForDeepReport}
 selectedInsightsCount={selectedInsightsForModal}
 subscriptionTier={subscriptionTier}
 />

 {/* Modal Upgrade Memory Pattern (FREE restriction) */}
 {showUpgradeModalMemory && (
 <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'var(--overlay)' }}>
 <div className="bg-theme-surface border border-theme p-8 max-w-md w-full">
 <div className="flex items-center justify-between mb-6">
 <div>
 <p className="page-kicker mb-2">Réservé</p>
 <h3 className="page-title text-2xl">Mémoire</h3>
 </div>
 <button
 onClick={() => setShowUpgradeModalMemory(false)}
 className="text-theme-secondary hover:text-theme-primary"
 >
 <X className="w-5 h-5" strokeWidth={1.5} />
 </button>
 </div>
 
 <p className="text-theme-secondary mb-6 text-sm leading-relaxed">
 Les analyses Mémoire, DNA Score et Collisions sont réservées aux plans Pro, Business et Enterprise.
 </p>
 
 <p className="text-theme-tertiary mb-6 text-xs">
 Vous pouvez consulter les patterns existants, mais pas déclencher de nouvelles analyses.
 </p>
 
 <div className="border border-theme p-4 mb-6">
 <p className="text-sm font-medium mb-2">Inclus dans l'abonnement</p>
 <ul className="text-theme-secondary text-xs space-y-1.5">
 <li>Détection automatique de patterns</li>
 <li>DNA Score et maturité</li>
 <li>Collisions et opportunités</li>
 <li>Intégration Notion</li>
 </ul>
 </div>
 
 <button
 onClick={() => {
 setShowUpgradeModalMemory(false);
 navigate('/billing');
 }}
 className="btn-modern btn-primary w-full"
 >
 Voir les plans
 </button>
 
 <button
 onClick={() => setShowUpgradeModalMemory(false)}
 className="w-full mt-3 px-6 py-2 text-theme-secondary hover:text-theme-primary text-sm"
 >
 Rester sur le plan Free
 </button>
 </div>
 </div>
 )}

 {/* MODAL PDF PREVIEW avec Upload automatique Scaleway */}
 {showPDFPreview && currentDeepReport && (
 <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'var(--overlay)' }}>
 <div className="w-full max-w-7xl h-[90vh] bg-theme-surface border border-theme overflow-hidden">
 <PDFPreviewV3
 content={markdownContent}
 options={{
 title: currentDeepReport.title || 'Deep Report Stratégique',
 subtitle: `Session: ${activeSessionId || 'Session courante'}`,
 author: user?.email || 'Clairify AI',
 timestamp: true
 }}
 reportId={currentDeepReport.id}
 sessionId={activeSessionId || undefined}
 onClose={() => setShowPDFPreview(false)}
 onPDFUploaded={(fileKey, downloadUrl) => {
 logger.log('[Studio] PDF uploadé vers Scaleway:', fileKey);
 setCurrentDeepReport({
 ...currentDeepReport,
 pdf_file_key: fileKey,
 pdf_url: downloadUrl,
 storage_provider: 'scaleway_s3'
 });
 }}
 />
 </div>
 </div>
 )}

 {/* MODAL SUPPRIMÉ - Rename/Delete maintenant inline dans la bulle */}
 </div>
 );
}